import { useEffect, useMemo, useState, type MouseEvent as ReactMouseEvent, type WheelEvent as ReactWheelEvent } from 'react';

import type { TopologyV2Edgeschema, TopologyV2Graphschema, TopologyV2Nodeschema } from '@app/types/sdk';

import type { TopologyCanvasProps } from './TopologyCanvas';

type PathRelatedViewRendererProps = Omit<TopologyCanvasProps, 'topology'> & {
  topology: TopologyV2Graphschema | null;
};

type TopologyNode = TopologyV2Nodeschema;
type TopologyEdge = TopologyV2Edgeschema;
type TopologyGraph = TopologyV2Graphschema;
type EndpointRole = 'source' | 'destination';

type NodeKind =
  | 'source'
  | 'destination'
  | 'device'
  | 'endpoint'
  | 'vrf'
  | 'interface'
  | 'subnet'
  | 'vlan'
  | 'domain'
  | 'other';

type CardKind = Exclude<NodeKind, 'interface'> | 'device';
type CloudResourceKind = 'tgw' | 'eni' | 'subnet' | 'vpc' | 'ec2' | 'vpn_connection' | 'tgw_attachment' | 'tunnel' | 'terminal';

type Point = { x: number; y: number };
type RectSide = 'left' | 'right' | 'top' | 'bottom';
type PathPortRole = 'default' | 'source' | 'destination' | 'source-destination';
type PathPortView = { key: string; x: number; y: number; role: PathPortRole };
export type PathRouteAnchor = Point & { side: RectSide; normal?: Point; portNodeId?: string };
export type PathRouteRect = { id?: string; left: number; right: number; top: number; bottom: number };
type PathInterfaceSlot = { column: number; row: number };
type PathInterfaceLayoutById = Record<string, PathInterfaceSlot>;
type PathCardVrfGroup = {
  node: TopologyNode;
  pathIds: string[];
  interfaces: TopologyNode[];
  interfaceLayoutById: PathInterfaceLayoutById;
};

type PathCard = {
  id: string;
  nodeId: string;
  node: TopologyNode;
  kind: CardKind;
  level: string;
  role?: string;
  pathIds: string[];
  memberNodeIds: string[];
  x: number;
  y: number;
  width: number;
  height: number;
  rank: number;
  vrfGroups: PathCardVrfGroup[];
  interfaces: TopologyNode[];
  interfaceLayoutById: PathInterfaceLayoutById;
  vlanChips: TopologyNode[];
  cloudResource?: CloudResourceKind;
  subnetVpc?: TopologyNode;
  vpnGroups: PathCardVrfGroup[];
};

type PathCardRoot = {
  id: string;
  node: TopologyNode;
};

type PathEdgeView = {
  id: string;
  edge: TopologyEdge;
  fromCardId: string;
  toCardId: string;
  label: string;
  hasExplicitLabel: boolean;
  pathIds: string[];
  kind: 'path' | 'support' | 'gap';
  color: string;
  from: PathRouteAnchor;
  to: PathRouteAnchor;
  route: Point[];
};

type PathFlowCardEdge = {
  edge: TopologyEdge;
  fromCardId: string;
  toCardId: string;
  fromNodeId: string;
  toNodeId: string;
  pathIds: string[];
  swapped?: boolean;
};

type PathInterfaceFlowRole = 'ingress' | 'egress';
type InterfaceFlowRolesByPath = Map<string, Map<string, Set<PathInterfaceFlowRole>>>;
type InterfaceFlowRoleOverrides = Map<string, PathInterfaceFlowRole>;

export type EvidenceGapView = {
  id: string;
  code: string;
  summary: string;
  severity?: string;
  family?: string;
  pathIds: string[];
  target?: string;
  collectedAfter?: string;
  requiredForEvidenceLevel?: string;
  source: string;
};

export type PathRelatedViewModel = {
  pathIds: string[];
  cards: PathCard[];
  edges: PathEdgeView[];
  evidenceGaps: EvidenceGapView[];
  nodeToCardId: Record<string, string>;
  width: number;
  height: number;
};

const PATH_RELATED_VIEW_SCHEMA = 'topology.path.related_view.v1';
const PATH_KIND_VALUES = new Set(['path', 'path_result', 'path_set', 'path_set_result']);
const DEVICE_WIDTH = 240;
const BASE_CARD_WIDTH = 210;
const BASE_CARD_HEIGHT = 104;
const SUBNET_CARD_SIZE = 172;
const DEVICE_HEADER_HEIGHT = 96;
const DEVICE_CARD_PADDING = 14;
const DEVICE_SECTION_GAP = 8;
const VRF_GROUP_PADDING = 8;
const VRF_GROUP_TITLE_HEIGHT = 12;
const VRF_GROUP_TITLE_GAP = 6;
const VRF_GROUP_BASE_HEIGHT = 28;
const INTERFACE_GRID_COLUMNS = 3;
const INTERFACE_GRID_GAP = 6;
const INTERFACE_CHIP_HEIGHT = 22;
const INTERFACE_ROW_HEIGHT = 28;
const INTERFACE_PORT_INSET_X = 13;
// Keep in sync with the rendered interface chip dot geometry.
const INTERFACE_PORT_RADIUS = 7;
const UNGROUPED_INTERFACE_TOP_OFFSET = 9;
const RANK_GAP = 290;
const ROW_GAP = 44;
const CANVAS_PAD_X = 56;
const CANVAS_PAD_Y = 54;
const EDGE_EXIT_PADDING = 46;
const EDGE_RECT_CLEARANCE = 18;
const PATH_FLOW_COLOR = '#2563eb';
const ROUTE_EPSILON = 0.001;
const MIN_ZOOM = 0.45;
const MAX_ZOOM = 1.6;
const ZOOM_STEP = 0.1;

const asRecord = (value: unknown): Record<string, unknown> | undefined => {
  if (!value || typeof value !== 'object' || Array.isArray(value)) {
    return undefined;
  }
  return value as Record<string, unknown>;
};

const stringValue = (value: unknown): string | undefined => (typeof value === 'string' ? value.trim() : undefined);

const normalized = (value: unknown): string =>
  (stringValue(value) ?? '').trim().toLowerCase().replace(/\s+/g, '_').replace(/-/g, '_');

const getLabels = (nodeOrEdge: { labels?: unknown }): Record<string, unknown> => asRecord(nodeOrEdge.labels) ?? {};
const getProperties = (nodeOrEdge: { properties?: unknown }): Record<string, unknown> => asRecord(nodeOrEdge.properties) ?? {};
const hasWordHint = (value: string, hints: string[]): boolean => hints.some((hint) => value === hint || value.includes(`_${hint}`) || value.includes(`${hint}_`));

const unique = <T extends string>(values: T[]): T[] => Array.from(new Set(values.filter((value): value is T => Boolean(value))));

const defaultInterfaceSlotForIndex = (interfaceIndex: number): PathInterfaceSlot => ({
  column: interfaceIndex % INTERFACE_GRID_COLUMNS,
  row: Math.floor(interfaceIndex / INTERFACE_GRID_COLUMNS)
});

const interfaceRowCount = (interfaces: TopologyNode[], interfaceLayoutById: PathInterfaceLayoutById = {}): number => {
  if (interfaces.length === 0) {
    return 0;
  }
  return interfaces.reduce((maxRows, intf, index) => {
    const slot = interfaceLayoutById[intf.id] ?? defaultInterfaceSlotForIndex(index);
    return Math.max(maxRows, slot.row + 1);
  }, 1);
};

const vrfGroupHeight = (group: PathCardVrfGroup): number => VRF_GROUP_BASE_HEIGHT + interfaceRowCount(group.interfaces, group.interfaceLayoutById) * INTERFACE_ROW_HEIGHT;

const ungroupedInterfaceHeight = (interfaces: TopologyNode[], interfaceLayoutById: PathInterfaceLayoutById): number =>
  interfaces.length > 0 ? UNGROUPED_INTERFACE_TOP_OFFSET - INTERFACE_GRID_GAP + interfaceRowCount(interfaces, interfaceLayoutById) * INTERFACE_ROW_HEIGHT : 0;

const interfaceGridColumnWidth = (containerWidth: number): number =>
  (containerWidth - (INTERFACE_GRID_COLUMNS - 1) * INTERFACE_GRID_GAP) / INTERFACE_GRID_COLUMNS;

const interfacePortColumnX = (left: number, containerWidth: number, column: number): number =>
  left + column * (interfaceGridColumnWidth(containerWidth) + INTERFACE_GRID_GAP) + Math.min(INTERFACE_PORT_INSET_X, interfaceGridColumnWidth(containerWidth) / 2);

const stringArray = (value: unknown): string[] => {
  if (Array.isArray(value)) {
    return value.map((item) => stringValue(item) ?? '').filter(Boolean);
  }
  const single = stringValue(value);
  return single ? [single] : [];
};

const elementPathMetadata = (value: unknown): Record<string, unknown> | undefined => {
  const record = asRecord(value);
  if (!record) {
    return undefined;
  }
  return asRecord(record.topologyPath);
};

const graphPathMetadata = (value: unknown): Record<string, unknown> | undefined => {
  const record = asRecord(value);
  if (!record) {
    return undefined;
  }
  return asRecord(record.path);
};

const pathIdsFromRecord = (record: Record<string, unknown> | undefined): string[] => {
  if (!record) {
    return [];
  }
  const directPathIds = unique(stringArray(record.pathIds));
  const nestedPathIds = Array.isArray(record.paths)
    ? unique(record.paths.map(asRecord).flatMap((path) => pathIdsFromRecord(path)))
    : [];
  if (directPathIds.length > 0 || nestedPathIds.length > 0) {
    return unique([...directPathIds, ...nestedPathIds]);
  }
  return unique(stringArray(record.pathId));
};

const pathIdsForNode = (node: TopologyNode): string[] => {
  const properties = getProperties(node);
  return pathIdsFromRecord(elementPathMetadata(properties));
};

const pathIdsForEdge = (edge: TopologyEdge): string[] => {
  const properties = getProperties(edge);
  return pathIdsFromRecord(elementPathMetadata(properties));
};

const endpointRolesFromValue = (value: unknown): EndpointRole[] =>
  unique(stringArray(value).map((role) => normalized(role)).filter((role): role is EndpointRole => role === 'source' || role === 'destination'));

const nodeEndpointRoles = (node: TopologyNode): EndpointRole[] => {
  const properties = getProperties(node);
  const meta = elementPathMetadata(properties);
  return endpointRolesFromValue(meta?.endpointRoles);
};

const evidenceGapRecords = (value: unknown): Record<string, unknown>[] =>
  Array.isArray(value) ? value.map(asRecord).filter((gap): gap is Record<string, unknown> => Boolean(gap)) : [];

const targetLabel = (target: unknown): string | undefined => {
  const record = asRecord(target);
  if (!record) {
    return undefined;
  }
  const kind = stringValue(record.kind ?? record.type);
  const display = stringValue(record.displayName ?? record.name);
  const id = stringValue(record.id ?? record.ref);
  if (kind && (display || id)) {
    return `${kind}:${display ?? id}`;
  }
  return display ?? id ?? kind;
};

const evidenceGapViewsFromRecord = (
  value: unknown,
  inheritedPathIds: string[],
  source: string
): EvidenceGapView[] => {
  const record = asRecord(value);
  if (!record) {
    return [];
  }
  return evidenceGapRecords(record.evidenceGaps).map((gap, index) => {
    const pathIds = unique([
      ...inheritedPathIds,
      ...pathIdsFromRecord(gap),
      ...pathIdsFromRecord(elementPathMetadata(gap))
    ]);
    const code = stringValue(gap.code) ?? `evidence_gap_${index + 1}`;
    return {
      id: `${source}:${code}:${index}`,
      code,
      summary: stringValue(gap.summary ?? gap.message ?? gap.reason ?? gap.detail) ?? code,
      severity: normalized(gap.severity) || undefined,
      family: stringValue(gap.family),
      pathIds,
      target: targetLabel(gap.target),
      collectedAfter: stringValue(gap.collectedAfter),
      requiredForEvidenceLevel: stringValue(gap.requiredForEvidenceLevel),
      source
    };
  });
};

const evidenceGapViewsFromPathPayload = (value: unknown, source: string): EvidenceGapView[] => {
  const record = asRecord(value);
  if (!record) {
    return [];
  }
  const gaps = evidenceGapViewsFromRecord(record, pathIdsFromRecord(record), source);
  const paths = Array.isArray(record.paths) ? record.paths : [];
  paths.forEach((rawPath, index) => {
    const path = asRecord(rawPath);
    if (!path) {
      return;
    }
    const pathIds = pathIdsFromRecord(path);
    evidenceGapViewsFromRecord(path, pathIds, `${source}:path:${index + 1}`).forEach((gap) => gaps.push(gap));
    const nestedContainers = [
      ...(Array.isArray(path.segments) ? path.segments : []),
      ...(Array.isArray(path.hops) ? path.hops : [])
    ];
    nestedContainers.forEach((rawContainer, containerIndex) => {
      const container = asRecord(rawContainer);
      if (!container) {
        return;
      }
      const containerPathIds = unique([...pathIds, ...pathIdsFromRecord(container)]);
      evidenceGapViewsFromRecord(container, containerPathIds, `${source}:path:${index + 1}:step:${containerIndex + 1}`).forEach((gap) =>
        gaps.push(gap)
      );
    });
  });
  return gaps;
};

const nodeLevel = (node: TopologyNode): string => {
  const labels = getLabels(node);
  return normalized(labels.level ?? labels.kind ?? labels.type);
};

const nodeRole = (node: TopologyNode): string | undefined => {
  const labels = getLabels(node);
  const role = normalized(labels.role ?? getProperties(node).role);
  return role || undefined;
};

const nodeTraceRole = (node: TopologyNode): string | undefined => {
  const explicitRole = nodeRole(node);
  if (explicitRole === 'source' || explicitRole === 'destination') {
    return explicitRole;
  }
  const endpointRoles = nodeEndpointRoles(node);
  if (endpointRoles.includes('source')) {
    return 'source';
  }
  if (endpointRoles.includes('destination')) {
    return 'destination';
  }
  return undefined;
};

const displayName = (node: TopologyNode): string => node.displayName || node.id;

const cloudResourceKind = (node: TopologyNode): CloudResourceKind | undefined => {
  const id = node.id.toLowerCase();
  const labels = getLabels(node);
  const properties = getProperties(node);
  const aws = asRecord(properties.aws);
  const refTypes = Array.isArray((node as { refs?: unknown }).refs)
    ? ((node as { refs?: unknown }).refs as unknown[])
        .map(asRecord)
        .map((ref) => normalized(ref?.type))
    : [];
  const hasRef = (type: string): boolean => refTypes.includes(type);
  if (id.includes('ref:aws.tgw_attachment:') || hasRef('aws.tgw_attachment')) return 'tgw_attachment';
  if (id.includes('ref:aws.tgw:') || hasRef('aws.tgw')) return 'tgw';
  if (id.includes('ref:aws.eni:') || hasRef('aws.eni')) return 'eni';
  if (id.includes('ref:aws.subnet:') || hasRef('aws.subnet')) return 'subnet';
  if (id.includes('ref:aws.vpc:') || hasRef('aws.vpc')) return 'vpc';
  if (id.includes('ref:aws.ec2_instance:') || hasRef('aws.ec2_instance')) return 'ec2';
  // A derived tunnel refers to its parent VPN connection through refs, but is
  // still the tunnel chip—not the VPN group that contains it.
  if (/^ref:derived:vpn_connection:\d{12}:/.test(id)) return 'tunnel';
  if (id.includes('ref:aws.vpn_connection:') || hasRef('aws.vpn_connection')) return 'vpn_connection';
  if (normalized(labels.domain ?? properties.domain) === 'aws') {
    if (id.includes('vpn_connection') || normalized(labels.level) === 'vpn_endpoint') return 'tunnel';
  }
  if (
    id.includes('ref:derived:l3:terminal:') ||
    /ref:aws\.(internet_gateway|nat_gateway|vpc_endpoint|egress_only_internet_gateway):/.test(id) ||
    normalized(labels.level).includes('terminal')
  ) return 'terminal';
  if (aws && (stringValue(aws.internetGatewayId) || stringValue(aws.natGatewayId) || stringValue(aws.vpcEndpointId))) return 'terminal';
  return undefined;
};

const cloudPresentationValue = (node: TopologyNode, key: string): string | undefined => {
  const presentation = asRecord(getProperties(node).presentation);
  const rows = Array.isArray(presentation?.rows) ? presentation.rows.map(asRecord) : [];
  const normalizedKey = normalized(key).replace(/_/g, '');
  const match = rows
    .filter((row): row is Record<string, unknown> => Boolean(row))
    .find((row) => normalized(row.key ?? row.label).replace(/_/g, '') === normalizedKey);
  return stringValue(match?.value);
};

const interfaceAddressValues = (node: TopologyNode): string[] => {
  const labels = getLabels(node);
  const properties = getProperties(node);
  const aws = asRecord(properties.aws);
  const awsPrivateIps = Array.isArray(aws?.privateIpAddresses)
    ? aws.privateIpAddresses.map(asRecord).flatMap((address) => stringArray(address?.privateIpAddress))
    : [];
  return unique([
    ...stringArray(properties.addresses),
    ...stringArray(properties.ip_addresses),
    ...stringArray(properties.ipAddresses),
    ...stringArray(properties.prefixes),
    ...stringArray(properties.cidrs),
    ...stringArray(properties.ip_address),
    ...stringArray(properties.ipAddress),
    ...stringArray(properties.address),
    ...stringArray(properties.prefix),
    ...stringArray(properties.cidr),
    ...stringArray(labels.ip_address),
    ...stringArray(labels.ipAddress),
    ...stringArray(labels.address),
    ...stringArray(labels.prefix),
    ...stringArray(labels.cidr),
    ...stringArray(aws?.privateIpAddress),
    ...awsPrivateIps,
    // Cloud tunnel chips intentionally expose only the inside IP on hover.
    ...stringArray(cloudPresentationValue(node, 'insideIp'))
  ]);
};

const interfaceAddressLabel = (address: string): string => address.split('/')[0]?.trim() || address;

const interfaceChipLabel = (node: TopologyNode): string => {
  const name = displayName(node).trim();
  if (name) {
    return name;
  }
  return interfaceAddressLabel(interfaceAddressValues(node)[0] ?? node.id);
};

const interfaceTitle = (node: TopologyNode): string => {
  const addresses = interfaceAddressValues(node);
  return addresses.length > 0 ? addresses.join(', ') : displayName(node);
};

const endpointIdentityValue = (node: TopologyNode): string =>
  displayName(node)
    .replace(/^(source|destination)\s*:\s*/i, '')
    .trim()
    .toLowerCase();

const nodeVrfKey = (node: TopologyNode): string | undefined => {
  const labels = getLabels(node);
  const properties = getProperties(node);
  const rawCandidates = [
    stringValue(labels.vrf),
    stringValue(properties.vrf),
    stringValue(labels.vrfName),
    stringValue(properties.vrfName),
    stringValue(labels.instance),
    stringValue(properties.instance),
    stringValue(labels.domain),
    stringValue(properties.domain),
    nodeKind(node) === 'vrf' ? displayName(node) : undefined
  ].filter((value): value is string => Boolean(value));

  for (const raw of rawCandidates) {
    const trimmed = raw.trim();
    if (!trimmed) {
      continue;
    }
    const withoutPrefix = trimmed.replace(/^vrf[:/_-]/i, '');
    const normalizedKey = normalized(withoutPrefix);
    if (normalizedKey && normalizedKey !== 'routing' && normalizedKey !== 'route') {
      return normalizedKey;
    }
  }
  return undefined;
};

const nodeKind = (node: TopologyNode): NodeKind => {
  const level = nodeLevel(node);
  const labels = getLabels(node);
  const properties = getProperties(node);
  const name = displayName(node).trim().toLowerCase();
  const instance = normalized(labels.instance ?? properties.instance);
  const domain = normalized(labels.domain ?? properties.domain);
  const semanticKind = normalized(labels.semanticKind ?? properties.semanticKind ?? labels.kind ?? properties.kind);
  const explicitRole = normalized(labels.role ?? properties.role);
  const endpointLike = level.includes('endpoint') || level === 'host' || hasWordHint(semanticKind, ['endpoint', 'host']);
  const endpointRoles = endpointLike ? nodeEndpointRoles(node) : [];
  const cloudKind = cloudResourceKind(node);
  const pathEndpointRole = endpointRoles.includes('source') ? 'source' : endpointRoles.includes('destination') ? 'destination' : undefined;
  if (explicitRole === 'source' || name.startsWith('source:') || (!explicitRole && pathEndpointRole === 'source')) {
    return 'source';
  }
  if (explicitRole === 'destination' || explicitRole === 'dest' || name.startsWith('destination:') || (!explicitRole && pathEndpointRole === 'destination')) {
    return 'destination';
  }
  // AWS uses the same generic levels as the on-prem graph (device, interface,
  // domain). Canonical resource identity is the stable way to retain the cloud
  // meaning without changing the backend response contract.
  if (cloudKind === 'tgw' || cloudKind === 'ec2') {
    return 'device';
  }
  if (cloudKind === 'eni' || cloudKind === 'tgw_attachment' || cloudKind === 'tunnel') {
    return 'interface';
  }
  if (cloudKind === 'subnet') {
    return 'subnet';
  }
  if (endpointLike) {
    return 'endpoint';
  }
  if (level.includes('device') || level === 'router' || level === 'switch' || level === 'firewall') {
    return 'device';
  }
  if (level.includes('interface') || level.includes('l3_interface') || /^(eth|ethernet|loopback|port-channel|vi)\d+/i.test(name)) {
    return 'interface';
  }
  if (level.includes('subnet') || /\d+\.\d+\.\d+\.\d+\/\d+/.test(name)) {
    return 'subnet';
  }
  if (level.includes('vlan') || level.includes('bridge') || /^vlan\s+\d+/i.test(displayName(node))) {
    return 'vlan';
  }
  const vrfNameHint = name === 'default' || /(^|[-_\s])vrf?($|[-_\s])/.test(name);
  const vrfMetadataHint =
    level.includes('vrf') ||
    instance.startsWith('vrf:') ||
    instance.startsWith('vrf_') ||
    hasWordHint(instance, ['vrf']) ||
    hasWordHint(domain, ['vrf']) ||
    hasWordHint(semanticKind, ['vrf']) ||
    Boolean(stringValue(labels.vrf ?? properties.vrf ?? labels.vrfName ?? properties.vrfName));
  if (vrfMetadataHint || ((level.includes('domain') || semanticKind.includes('domain')) && vrfNameHint)) {
    return 'vrf';
  }
  if (level.includes('domain') || level.includes('tunnel') || level.includes('cloud') || level.includes('service')) {
    return 'domain';
  }
  return 'other';
};

const referenceIDsOfType = (node: TopologyNode, type: string): string[] =>
  Array.isArray((node as { refs?: unknown }).refs)
    ? ((node as { refs?: unknown }).refs as unknown[])
        .map(asRecord)
        .filter((ref): ref is Record<string, unknown> => normalized(ref?.type) === normalized(type))
        .map((ref) => stringValue(ref.id))
        .filter((id): id is string => Boolean(id))
    : [];

// A logical tunnel endpoint and its L3 tunnel interface may be distinct graph
// nodes. The backend supplies their common inventory-interface reference; this
// aliases the two for presentation only, without matching names or site IDs.
const tunnelInterfaceAliasesFromReferences = (nodesById: Map<string, TopologyNode>): Map<string, string> => {
  const l3TunnelByInventoryInterfaceID = new Map<string, string>();
  nodesById.forEach((node) => {
    const interfaceKind = normalized(getLabels(node)['if.kind'] ?? getProperties(node)['if.kind']);
    if (nodeKind(node) !== 'interface' || interfaceKind !== 'tunnel') return;
    referenceIDsOfType(node, 'inventory.interface').forEach((referenceID) => {
      if (!l3TunnelByInventoryInterfaceID.has(referenceID)) {
        l3TunnelByInventoryInterfaceID.set(referenceID, node.id);
      }
    });
  });

  const aliases = new Map<string, string>();
  nodesById.forEach((node) => {
    const interfaceKind = normalized(getLabels(node)['if.kind'] ?? getProperties(node)['if.kind']);
    if (nodeKind(node) === 'interface' || interfaceKind !== 'tunnel') return;
    const alias = referenceIDsOfType(node, 'inventory.interface')
      .map((referenceID) => l3TunnelByInventoryInterfaceID.get(referenceID))
      .find((interfaceID): interfaceID is string => Boolean(interfaceID));
    if (alias) aliases.set(node.id, alias);
  });
  return aliases;
};

const interfaceEndpointRoleMap = (graph: TopologyGraph, selectedPathId: string | 'all' = 'all'): Map<string, EndpointRole[]> => {
  const nodesById = new Map(graph.nodes.map((node) => [node.id, node]));
  const rolesByInterfaceId = new Map<string, Set<EndpointRole>>();
  const addRole = (nodeId: string, role: EndpointRole) => {
    const node = nodesById.get(nodeId);
    if (!node || nodeKind(node) !== 'interface') {
      return;
    }
    if (!rolesByInterfaceId.has(nodeId)) {
      rolesByInterfaceId.set(nodeId, new Set());
    }
    rolesByInterfaceId.get(nodeId)?.add(role);
  };

  graph.nodes.forEach((node) => {
    const nodePathIds = pathIdsForNode(node);
    if (selectedPathId !== 'all' && nodePathIds.length > 0 && !nodePathIds.includes(selectedPathId)) {
      return;
    }
    nodeEndpointRoles(node).forEach((role) => addRole(node.id, role));
  });

  graph.edges.forEach((edge) => {
    const edgePathIds = pathIdsForEdge(edge);
    if (selectedPathId !== 'all' && edgePathIds.length > 0 && !edgePathIds.includes(selectedPathId)) {
      return;
    }
    const from = nodesById.get(edge.fromId);
    const to = nodesById.get(edge.toId);
    if (!from || !to) {
      return;
    }
    const fromKind = nodeKind(from);
    const toKind = nodeKind(to);
    if (fromKind === 'source' && toKind === 'interface') {
      addRole(to.id, 'source');
    } else if (fromKind === 'destination' && toKind === 'interface') {
      addRole(to.id, 'destination');
    } else if (toKind === 'source' && fromKind === 'interface') {
      addRole(from.id, 'source');
    } else if (toKind === 'destination' && fromKind === 'interface') {
      addRole(from.id, 'destination');
    }
  });

  return new Map(Array.from(rolesByInterfaceId.entries()).map(([nodeId, roles]) => [nodeId, Array.from(roles.values())]));
};

const interfaceRolePriority = (roles: EndpointRole[]): number => {
  const hasSource = roles.includes('source');
  const hasDestination = roles.includes('destination');
  if (hasSource && !hasDestination) {
    return 0;
  }
  if (hasSource && hasDestination) {
    return 1;
  }
  if (!hasSource && !hasDestination) {
    return 2;
  }
  return 3;
};

const compareInterfacesForDisplay = (
  left: TopologyNode,
  right: TopologyNode,
  rolesById: Map<string, EndpointRole[]>
): number => {
  const byRole = interfaceRolePriority(rolesById.get(left.id) ?? []) - interfaceRolePriority(rolesById.get(right.id) ?? []);
  if (byRole !== 0) {
    return byRole;
  }
  return interfaceChipLabel(left).localeCompare(interfaceChipLabel(right), undefined, { numeric: true, sensitivity: 'base' });
};

const edgeRelation = (edge: TopologyEdge): string => {
  const labels = getLabels(edge);
  const properties = getProperties(edge);
  const relation =
    labels.relation ??
    labels.relationship ??
    labels.kind ??
    labels.type ??
    properties.relation ??
    properties.relationship ??
    properties.kind;
  return normalized(relation);
};

const pathPresentationEdgeRole = (edge: TopologyEdge): string =>
  normalized(asRecord(getProperties(edge).topologyPath)?.edgeRole);

// Context edges preserve grouping facts, such as VLAN -> routed-interface ->
// subnet, but are not packet-flow arrows.
const isPathPresentationContextEdge = (edge: TopologyEdge): boolean => pathPresentationEdgeRole(edge) === 'context';

const edgeLabel = (edge: TopologyEdge): string => {
  const labels = getLabels(edge);
  return (
    stringValue(labels.label) ??
    stringValue(labels.relationship) ??
    stringValue(labels.relation) ??
    stringValue(labels.kind) ??
    edgeRelation(edge) ??
    ''
  );
};

const hasExplicitEdgeLabel = (edge: TopologyEdge): boolean => Boolean(stringValue(getLabels(edge).label));

const pathRelatedEdgeLabel = (edge: TopologyEdge, kind: PathEdgeView['kind']): string => {
  if (isSupportRelation(edgeRelation(edge)) || isPresentationSequenceEdge(edge)) {
    return '';
  }
  return edgeLabel(edge);
};

const isSupportRelation = (relation: string): boolean =>
  [
    'contains',
    'hosts',
    'member_of',
    'memberof',
    'applies_to',
    'endpoint_anchor',
    'belongs_to',
    'in_vrf',
    'attached_to',
    'depends_on'
  ].includes(relation);

const isStructuralContainmentEdge = (edge: TopologyEdge, nodesById: Map<string, TopologyNode>): boolean => {
  const relation = edgeRelation(edge);
  if (!isSupportRelation(relation)) {
    return false;
  }
  const from = nodesById.get(edge.fromId);
  const to = nodesById.get(edge.toId);
  if (!from || !to) {
    return false;
  }
  const left = nodeKind(from);
  const right = nodeKind(to);
  return (
    (left === 'device' && right === 'vrf') ||
    (left === 'vrf' && right === 'device') ||
    (left === 'device' && right === 'interface') ||
    (left === 'interface' && right === 'device') ||
    (left === 'vrf' && right === 'interface') ||
    (left === 'interface' && right === 'vrf')
  );
};

const isGapEdge = (edge: TopologyEdge): boolean => {
  const relation = edgeRelation(edge);
  const properties = getProperties(edge);
  return relation.includes('gap') || properties.evidenceGap === true || properties.gap === true;
};

const edgeAppliesToSelectedPath = (edgePathIds: string[], selectedPathId: string | 'all'): boolean =>
  selectedPathId === 'all' || edgePathIds.includes(selectedPathId);

const isPathFlowEdge = (edge: TopologyEdge, edgePathIds: string[]): boolean =>
  edge.directed === true && edgePathIds.length > 0 && !isGapEdge(edge) && !isPathPresentationContextEdge(edge);

const orientPathFlowCardEdge = (
  candidate: PathFlowCardEdge,
  cardKindById: Map<string, CardKind>,
  cardRoleById: Map<string, string | undefined>,
  sourceSubnetCardIds: Set<string>,
  destinationSubnetCardIds: Set<string>,
  interfaceFlowRolesByPath: InterfaceFlowRolesByPath,
  selectedPathId: string | 'all'
): PathFlowCardEdge => {
  // The backend hop list is already ordered. Never reinterpret a visual
  // sequence edge from ingress/egress containment heuristics.
  if (isPresentationSequenceEdge(candidate.edge)) {
    return candidate;
  }
  const fromKind = cardKindById.get(candidate.fromCardId);
  const toKind = cardKindById.get(candidate.toCardId);
  const fromRole = cardRoleById.get(candidate.fromCardId);
  const toRole = cardRoleById.get(candidate.toCardId);
  const swap = (): PathFlowCardEdge => ({
    ...candidate,
    fromCardId: candidate.toCardId,
    toCardId: candidate.fromCardId,
    fromNodeId: candidate.toNodeId,
    toNodeId: candidate.fromNodeId,
    swapped: true
  });

  if (fromRole === 'source' || fromKind === 'source' || toRole === 'destination' || toKind === 'destination') {
    return candidate;
  }
  if (toRole === 'source' || toKind === 'source' || fromRole === 'destination' || fromKind === 'destination') {
    return swap();
  }

  const fromSubnet = fromKind === 'subnet' || fromKind === 'vlan';
  const toSubnet = toKind === 'subnet' || toKind === 'vlan';
  const fromDevice = fromKind === 'device';
  const toDevice = toKind === 'device';
  const interfaceFlowRole = interfaceFlowRoleForCandidate(candidate, interfaceFlowRolesByPath, selectedPathId);

  // Path direction must come from hop ingress/egress semantics, not from structural
  // interface/subnet relations such as member_of, which only tell us adjacency.
  if (interfaceFlowRole && ((fromDevice && toSubnet) || (fromSubnet && toDevice))) {
    const wantsDeviceToSubnet = interfaceFlowRole === 'egress';
    if (wantsDeviceToSubnet) {
      return fromDevice && toSubnet ? candidate : swap();
    }
    return fromSubnet && toDevice ? candidate : swap();
  }

  if (fromSubnet && toDevice && destinationSubnetCardIds.has(candidate.fromCardId) && !sourceSubnetCardIds.has(candidate.fromCardId)) {
    return swap();
  }
  if (fromDevice && toSubnet && sourceSubnetCardIds.has(candidate.toCardId) && !destinationSubnetCardIds.has(candidate.toCardId)) {
    return swap();
  }

  return candidate;
};

const edgeColor = (kind: PathEdgeView['kind']): string => {
  if (kind === 'gap') {
    return '#d97706';
  }
  if (kind !== 'path') {
    return '#94a3b8';
  }
  return PATH_FLOW_COLOR;
};

const hasPathSignal = (graph: TopologyGraph): boolean => {
  const meta = asRecord(graph.meta);
  const pathMeta = graphPathMetadata(meta);
  return (
    pathIdsFromRecord(meta).length > 0 ||
    pathIdsFromRecord(pathMeta).length > 0 ||
    graph.nodes.some((node) => pathIdsForNode(node).length > 0) ||
    graph.edges.some((edge) => pathIdsForEdge(edge).length > 0)
  );
};

const graphPathIds = (graph: TopologyGraph): string[] => {
  const graphMeta = asRecord(graph.meta);
  const pathMeta = graphPathMetadata(graphMeta);
  return unique([
    ...pathIdsFromRecord(graphMeta),
    ...pathIdsFromRecord(pathMeta),
    ...graph.nodes.flatMap(pathIdsForNode),
    ...graph.edges.flatMap(pathIdsForEdge)
  ]);
};

const topologyRefId = (value: unknown): string | undefined => {
  const record = asRecord(value);
  return stringValue(record?.id ?? record?.ref);
};

const pathHopKey = (pathId: string, hopIndex: number): string => `${pathId}:${hopIndex}`;

const interfaceFlowRolesFromGraphMeta = (graphMeta: Record<string, unknown>): InterfaceFlowRolesByPath => {
  const out: InterfaceFlowRolesByPath = new Map();
  const pathMeta = graphPathMetadata(graphMeta);
  if (!pathMeta) {
    return out;
  }
  const pathRecords = Array.isArray(pathMeta.paths) ? pathMeta.paths.map(asRecord).filter((record): record is Record<string, unknown> => Boolean(record)) : [];
  const addRole = (interfaceId: string, pathId: string, role: PathInterfaceFlowRole) => {
    if (!out.has(interfaceId)) {
      out.set(interfaceId, new Map());
    }
    const rolesByPathId = out.get(interfaceId)!;
    if (!rolesByPathId.has(pathId)) {
      rolesByPathId.set(pathId, new Set());
    }
    rolesByPathId.get(pathId)?.add(role);
  };

  pathRecords.forEach((pathRecord, pathIndex) => {
    const pathId = stringValue(pathRecord.pathId ?? pathRecord.id) ?? `path:${pathIndex}`;
    const hops = Array.isArray(pathRecord.hops) ? pathRecord.hops.map(asRecord).filter((record): record is Record<string, unknown> => Boolean(record)) : [];
    hops.forEach((hop) => {
      (['ingress', 'egress'] as const).forEach((role) => {
        const interfaceId = topologyRefId(hop[role]);
        if (interfaceId) {
          addRole(interfaceId, pathId, role);
        }
      });
    });
  });

  return out;
};

// The stable on-prem L3 graph already owns its forwarding edges. Cloud graph
// edges are connectivity/containment facts, so only cloud-touching portions of
// the backend-selected hop list need presentation edges here.
const cloudHopSequenceEdgesFromGraphMeta = (
  graphMeta: Record<string, unknown>,
  selectedPathId: string | 'all',
  terminalInterfaceIdByPathId: Map<string, string>,
  nodesById: Map<string, TopologyNode>,
  cardIdByPathHop: Map<string, string>
): TopologyEdge[] => {
  const pathMeta = graphPathMetadata(graphMeta);
  if (!pathMeta || !Array.isArray(pathMeta.paths)) return [];
  const out: TopologyEdge[] = [];
  pathMeta.paths.forEach((rawPath, pathIndex) => {
    const path = asRecord(rawPath);
    if (!path) return;
    const pathId = stringValue(path.pathId ?? path.id) ?? `path:${pathIndex}`;
    if (selectedPathId !== 'all' && pathId !== selectedPathId) return;
    const allHopNodeIds = (Array.isArray(path.hops) ? path.hops : [])
      .map(asRecord)
      .map((hop) => topologyRefId(hop?.node))
      .filter((nodeId): nodeId is string => Boolean(nodeId));
    const terminalInterfaceId = terminalInterfaceIdByPathId.get(pathId);
    const terminalIndex = terminalInterfaceId ? allHopNodeIds.indexOf(terminalInterfaceId) : -1;
    const hopNodeIds = terminalIndex >= 0 ? allHopNodeIds.slice(0, terminalIndex + 1) : allHopNodeIds;
    hopNodeIds.slice(1).forEach((toId, index) => {
      const fromId = hopNodeIds[index];
      if (!fromId || fromId === toId) return;
      const from = nodesById.get(fromId);
      const to = nodesById.get(toId);
      // Includes the on-prem-to-cloud handoff because the cloud tunnel is a
      // cloud resource; excludes all Core/PAN/on-prem subnet transitions.
      if (!((from && cloudResourceKind(from)) || (to && cloudResourceKind(to)))) return;
      out.push({
        id: `path-hop:${pathId}:${index + 1}:${fromId}:${toId}`,
        fromId,
        toId,
        directed: true,
        labels: { relation: 'forwarding' },
        properties: {
          topologyPath: { pathIds: [pathId] },
          presentation: {
            synthetic: 'ordered_hop',
            fromCardId: cardIdByPathHop.get(pathHopKey(pathId, index)),
            toCardId: cardIdByPathHop.get(pathHopKey(pathId, index + 1))
          }
        }
      } as unknown as TopologyEdge);
    });
  });
  return out;
};

const isPresentationSequenceEdge = (edge: TopologyEdge): boolean => edge.id.startsWith('path-hop:');

type RepeatedDeviceStage = {
  key: string;
  vrfId: string;
  interfaceIds: string[];
};

type RepeatedCloudStage = {
  key: string;
  resourceId: string;
  interfaceIds: string[];
  interfaceFlowRoles: InterfaceFlowRoleOverrides;
};

type RepeatedCloudStages = {
  stagesByResourceId: Map<string, RepeatedCloudStage[]>;
  cardIdByPathHop: Map<string, string>;
};

// The backend can deliberately visit the same visible cloud forwarding node
// more than once: most commonly TGW -> attachment ENI -> subnet -> attachment
// ENI -> the same TGW, but a subnet can also recur.  Give each returned
// occurrence its own presentation card so layout preserves hop order; this
// consumes no routing information beyond paths[].hops.
const repeatedCloudStagesFromGraphMeta = (
  graphMeta: Record<string, unknown>,
  selectedPathId: string | 'all',
  nodesById: Map<string, TopologyNode>,
  directDeviceByNodeId: Map<string, string>
): RepeatedCloudStages => {
  const out: RepeatedCloudStages = { stagesByResourceId: new Map(), cardIdByPathHop: new Map() };
  const pathMeta = graphPathMetadata(graphMeta);
  if (!pathMeta || !Array.isArray(pathMeta.paths)) return out;

  pathMeta.paths.forEach((rawPath, pathIndex) => {
    const path = asRecord(rawPath);
    if (!path) return;
    const pathId = stringValue(path.pathId ?? path.id) ?? `path:${pathIndex}`;
    if (selectedPathId !== 'all' && pathId !== selectedPathId) return;
    const hops = (Array.isArray(path.hops) ? path.hops : [])
      .map(asRecord)
      .filter((hop): hop is Record<string, unknown> => Boolean(hop));
    const visitIndexesByResourceId = new Map<string, number[]>();
    hops.forEach((hop, hopIndex) => {
      const nodeId = topologyRefId(hop.node);
      const node = nodeId ? nodesById.get(nodeId) : undefined;
      const kind = node ? cloudResourceKind(node) : undefined;
      if (!node || (kind !== 'tgw' && kind !== 'subnet')) return;
      const indexes = visitIndexesByResourceId.get(node.id) ?? [];
      indexes.push(hopIndex);
      visitIndexesByResourceId.set(node.id, indexes);
    });

    visitIndexesByResourceId.forEach((visitIndexes, resourceId) => {
      if (visitIndexes.length < 2) return;
      const visitOccurrenceBySignature = new Map<string, number>();
      const stages = visitIndexes.map((hopIndex) => {
        const hop = hops[hopIndex]!;
        const interfaceIds: string[] = [];
        const interfaceFlowRoles: InterfaceFlowRoleOverrides = new Map();
        const ingressId = topologyRefId(hop.ingress);
        const egressId = topologyRefId(hop.egress);
        (['ingress', 'egress'] as const).forEach((role) => {
          const interfaceId = topologyRefId(hop[role]);
          if (!interfaceId || directDeviceByNodeId.get(interfaceId) !== resourceId) return;
          interfaceIds.push(interfaceId);
          interfaceFlowRoles.set(interfaceId, role);
        });
        // Candidate paths often converge onto the same downstream cloud hop.
        // Share that visual stage across candidates when its concrete ingress
        // and egress are identical; retain an occurrence suffix for a genuine
        // repeated loop with the same signature in one path.
        const signature = `${resourceId}:${ingressId ?? '-'}:${egressId ?? '-'}`;
        const signatureOccurrence = (visitOccurrenceBySignature.get(signature) ?? 0) + 1;
        visitOccurrenceBySignature.set(signature, signatureOccurrence);
        const key = `path-stage:cloud:${signature}:${signatureOccurrence}`;
        out.cardIdByPathHop.set(pathHopKey(pathId, hopIndex), key);
        return { key, resourceId, interfaceIds: unique(interfaceIds), interfaceFlowRoles };
      });
      const existingStages = out.stagesByResourceId.get(resourceId) ?? [];
      stages.forEach((stage) => {
        const existing = existingStages.find((candidate) => candidate.key === stage.key);
        if (!existing) {
          existingStages.push(stage);
          return;
        }
        stage.interfaceIds.forEach((interfaceId) => {
          if (!existing.interfaceIds.includes(interfaceId)) existing.interfaceIds.push(interfaceId);
          const role = stage.interfaceFlowRoles.get(interfaceId);
          if (role) existing.interfaceFlowRoles.set(interfaceId, role);
        });
      });
      out.stagesByResourceId.set(resourceId, existingStages);

      const resource = nodesById.get(resourceId);
      if (!resource || cloudResourceKind(resource) !== 'tgw') return;

      hops.forEach((hop, hopIndex) => {
        const nodeId = topologyRefId(hop.node);
        if (!nodeId || directDeviceByNodeId.get(nodeId) !== resourceId) return;
        const previousStage = out.cardIdByPathHop.get(pathHopKey(pathId, hopIndex - 1));
        const nextStage = out.cardIdByPathHop.get(pathHopKey(pathId, hopIndex + 1));
        // An ENI immediately after a TGW is that visit's egress.  An ENI
        // immediately before a TGW is the next visit's ingress.
        if (previousStage) out.cardIdByPathHop.set(pathHopKey(pathId, hopIndex), previousStage);
        else if (nextStage) out.cardIdByPathHop.set(pathHopKey(pathId, hopIndex), nextStage);
      });
    });
  });
  return out;
};

type DeviceStageVisit = {
  vrfId: string;
  interfaceIds: string[];
};

const repeatedDeviceStagesFromGraphMeta = (
  graphMeta: Record<string, unknown>,
  selectedPathId: string | 'all',
  nodesById: Map<string, TopologyNode>,
  directDeviceByNodeId: Map<string, string>,
  vrfByInterfaceId: Map<string, string>
): Map<string, RepeatedDeviceStage[]> => {
  const pathMeta = graphPathMetadata(graphMeta);
  if (!pathMeta) {
    return new Map();
  }
  const pathRecords = Array.isArray(pathMeta.paths)
    ? pathMeta.paths.map(asRecord).filter((record): record is Record<string, unknown> => Boolean(record))
    : [];
  const pathVisits: Array<Map<string, DeviceStageVisit[]>> = [];

  pathRecords.forEach((pathRecord, pathIndex) => {
    const pathId = stringValue(pathRecord.pathId ?? pathRecord.id) ?? `path:${pathIndex}`;
    if (selectedPathId !== 'all' && pathId !== selectedPathId) {
      return;
    }
    const visitsByDeviceId = new Map<string, DeviceStageVisit[]>();
    const hops = Array.isArray(pathRecord.hops)
      ? pathRecord.hops.map(asRecord).filter((record): record is Record<string, unknown> => Boolean(record))
      : [];

    hops.forEach((hop) => {
      const hopNodeId = topologyRefId(hop.node);
      const hopNode = hopNodeId ? nodesById.get(hopNodeId) : undefined;
      const deviceId =
        hopNodeId && hopNode && nodeKind(hopNode) === 'device'
          ? hopNodeId
          : hopNodeId
            ? directDeviceByNodeId.get(hopNodeId)
            : undefined;
      if (!deviceId) {
        return;
      }
      const interfaceIds = unique([topologyRefId(hop.ingress), topologyRefId(hop.egress)].filter((id): id is string => Boolean(id)));
      const hopVrfIds = unique(
        interfaceIds
          .filter((interfaceId) => directDeviceByNodeId.get(interfaceId) === deviceId)
          .map((interfaceId) => vrfByInterfaceId.get(interfaceId))
          .filter((vrfId): vrfId is string => Boolean(vrfId))
      );
      if (hopVrfIds.length !== 1) {
        return;
      }
      const visits = visitsByDeviceId.get(deviceId) ?? [];
      visits.push({ vrfId: hopVrfIds[0], interfaceIds });
      visitsByDeviceId.set(deviceId, visits);
    });
    pathVisits.push(visitsByDeviceId);
  });

  const stagesByDeviceId = new Map<string, Map<string, RepeatedDeviceStage>>();
  if (selectedPathId === 'all') {
    const devicesNeedingVrfStages = new Set<string>();
    pathVisits.forEach((visitsByDeviceId) => {
      visitsByDeviceId.forEach((visits, deviceId) => {
        if (visits.length > 1 && new Set(visits.map((visit) => visit.vrfId)).size > 1) {
          devicesNeedingVrfStages.add(deviceId);
        }
      });
    });
    devicesNeedingVrfStages.forEach((deviceId) => {
      const stages = new Map<string, RepeatedDeviceStage>();
      pathVisits.forEach((visitsByDeviceId) => {
        (visitsByDeviceId.get(deviceId) ?? []).forEach((visit) => {
          const key = `vrf:${visit.vrfId}`;
          const existing = stages.get(key);
          stages.set(key, {
            key,
            vrfId: visit.vrfId,
            interfaceIds: unique([...(existing?.interfaceIds ?? []), ...visit.interfaceIds])
          });
        });
      });
      stagesByDeviceId.set(deviceId, stages);
    });
  } else {
    pathVisits.forEach((visitsByDeviceId) => {
      visitsByDeviceId.forEach((visits, deviceId) => {
        if (visits.length < 2) {
          return;
        }
        const interfaceIds = visits.flatMap((visit) => visit.interfaceIds);
        if (new Set(interfaceIds).size !== interfaceIds.length) {
          return;
        }
        const stages = stagesByDeviceId.get(deviceId) ?? new Map<string, RepeatedDeviceStage>();
        stagesByDeviceId.set(deviceId, stages);
        const occurrencesByVrfId = new Map<string, number>();
        visits.forEach((visit) => {
          const occurrence = (occurrencesByVrfId.get(visit.vrfId) ?? 0) + 1;
          occurrencesByVrfId.set(visit.vrfId, occurrence);
          // VRF alone is not a stage identity: a path may leave and later
          // re-enter the same device/VRF through different interfaces.
          const key = `${visit.vrfId}:${occurrence}`;
          const existing = stages.get(key);
          stages.set(key, {
            key,
            vrfId: visit.vrfId,
            interfaceIds: unique([...(existing?.interfaceIds ?? []), ...visit.interfaceIds])
          });
        });
      });
    });
  }

  return new Map(
    Array.from(stagesByDeviceId.entries()).map(([deviceId, stages]) => [deviceId, Array.from(stages.values())])
  );
};

const interfaceFlowRoleForCandidate = (
  candidate: PathFlowCardEdge,
  interfaceFlowRolesByPath: InterfaceFlowRolesByPath,
  selectedPathId: string | 'all'
): PathInterfaceFlowRole | undefined => {
  const interfaceNodeIds = unique(
    [candidate.fromNodeId, candidate.toNodeId].filter((nodeId): nodeId is string => interfaceFlowRolesByPath.has(nodeId))
  );
  if (interfaceNodeIds.length !== 1 || candidate.pathIds.length === 0) {
    return undefined;
  }
  const relevantPathIds =
    selectedPathId === 'all'
      ? candidate.pathIds
      : candidate.pathIds.includes(selectedPathId)
        ? [selectedPathId]
        : [];
  if (relevantPathIds.length === 0) {
    return undefined;
  }
  const roles = new Set<PathInterfaceFlowRole>();
  relevantPathIds.forEach((pathId) => {
    interfaceFlowRolesByPath
      .get(interfaceNodeIds[0])
      ?.get(pathId)
      ?.forEach((role) => roles.add(role));
  });
  if (roles.size !== 1) {
    return undefined;
  }
  return Array.from(roles)[0];
};

const interfaceFlowRolesForSelection = (
  interfaceId: string,
  interfaceFlowRolesByPath: InterfaceFlowRolesByPath,
  selectedPathId: string | 'all'
): Set<PathInterfaceFlowRole> => {
  const rolesByPathId = interfaceFlowRolesByPath.get(interfaceId);
  const roles = new Set<PathInterfaceFlowRole>();
  if (!rolesByPathId) {
    return roles;
  }
  if (selectedPathId === 'all') {
    rolesByPathId.forEach((pathRoles) => pathRoles.forEach((role) => roles.add(role)));
    return roles;
  }
  rolesByPathId.get(selectedPathId)?.forEach((role) => roles.add(role));
  return roles;
};

export const graphUsesPathRelatedViewRenderer = (graph: TopologyGraph | null | undefined): boolean => {
  if (!graph) {
    return false;
  }
  const meta = asRecord(graph.meta);
  const schema = normalized(meta?.schema);
  const pathMeta = graphPathMetadata(meta);
  const kind = normalized(meta?.kind ?? pathMeta?.kind);
  const relatedView = schema === PATH_RELATED_VIEW_SCHEMA;
  const explicitlyNonPath = kind.length > 0 && !PATH_KIND_VALUES.has(kind);
  return relatedView && !explicitlyNonPath && hasPathSignal(graph);
};

const findContainment = (nodesById: Map<string, TopologyNode>, edges: TopologyEdge[]) => {
  const directDeviceByNodeId = new Map<string, string>();
  const vrfByInterfaceId = new Map<string, string>();
  const deviceByVrfId = new Map<string, string>();
  const vrfNodeIdsByKey = new Map<string, string[]>();

  nodesById.forEach((node) => {
    if (nodeKind(node) !== 'vrf') {
      return;
    }
    const key = nodeVrfKey(node);
    if (!key) {
      return;
    }
    if (!vrfNodeIdsByKey.has(key)) {
      vrfNodeIdsByKey.set(key, []);
    }
    vrfNodeIdsByKey.get(key)?.push(node.id);
  });

  edges.forEach((edge) => {
    const relation = edgeRelation(edge);
    if (!isSupportRelation(relation)) {
      return;
    }
    const from = nodesById.get(edge.fromId);
    const to = nodesById.get(edge.toId);
    if (!from || !to) {
      return;
    }
    const fromKind = nodeKind(from);
    const toKind = nodeKind(to);

    if (fromKind === 'device' && toKind === 'interface') {
      directDeviceByNodeId.set(to.id, from.id);
    } else if (toKind === 'device' && fromKind === 'interface') {
      directDeviceByNodeId.set(from.id, to.id);
    }

    if (fromKind === 'device' && toKind === 'vrf') {
      directDeviceByNodeId.set(to.id, from.id);
      deviceByVrfId.set(to.id, from.id);
    } else if (toKind === 'device' && fromKind === 'vrf') {
      directDeviceByNodeId.set(from.id, to.id);
      deviceByVrfId.set(from.id, to.id);
    }

    if (fromKind === 'vrf' && toKind === 'interface') {
      vrfByInterfaceId.set(to.id, from.id);
    } else if (toKind === 'vrf' && fromKind === 'interface') {
      vrfByInterfaceId.set(from.id, to.id);
    }
  });

  nodesById.forEach((node) => {
    if (nodeKind(node) !== 'interface' || vrfByInterfaceId.has(node.id)) {
      return;
    }
    const key = nodeVrfKey(node);
    if (!key) {
      return;
    }
    const candidateVrfIds = vrfNodeIdsByKey.get(key) ?? [];
    if (candidateVrfIds.length === 0) {
      return;
    }
    const deviceId = directDeviceByNodeId.get(node.id);
    const vrfId = candidateVrfIds.find((candidateId) => deviceId && deviceByVrfId.get(candidateId) === deviceId) ?? candidateVrfIds[0];
    vrfByInterfaceId.set(node.id, vrfId);
  });

  vrfByInterfaceId.forEach((vrfId, interfaceId) => {
    const interfaceDeviceId = directDeviceByNodeId.get(interfaceId);
    if (interfaceDeviceId && !deviceByVrfId.has(vrfId)) {
      deviceByVrfId.set(vrfId, interfaceDeviceId);
      directDeviceByNodeId.set(vrfId, interfaceDeviceId);
    }
    const deviceId = deviceByVrfId.get(vrfId);
    if (deviceId && !directDeviceByNodeId.has(interfaceId)) {
      directDeviceByNodeId.set(interfaceId, deviceId);
    }
  });

  return { directDeviceByNodeId, vrfByInterfaceId, deviceByVrfId };
};

type CloudPresentationContainment = {
  directDeviceByNodeId: Map<string, string>;
  tgwByVpnConnectionId: Map<string, string>;
  vpnConnectionByTunnelId: Map<string, string>;
  vpcBySubnetId: Map<string, string>;
};

// Cloud containment is intentionally isolated from the shared on-prem L3
// containment helper. It only assigns cloud rendering ownership; it does not
// contribute forwarding edges or choose a route.
const findCloudPresentationContainment = (
  nodesById: Map<string, TopologyNode>,
  edges: TopologyEdge[]
): CloudPresentationContainment => {
  const tgwByAttachmentId = new Map<string, string>();
  const attachmentByEniId = new Map<string, string>();
  const tgwByVpnConnectionId = new Map<string, string>();
  const vpnConnectionByTunnelId = new Map<string, string>();
  const vpcBySubnetId = new Map<string, string>();
  const directDeviceByNodeId = new Map<string, string>();

  edges.forEach((edge) => {
    const relation = edgeRelation(edge);
    if (!isSupportRelation(relation) && relation !== 'connects' && relation !== 'connect') return;
    const from = nodesById.get(edge.fromId);
    const to = nodesById.get(edge.toId);
    if (!from || !to) return;
    const fromKind = cloudResourceKind(from);
    const toKind = cloudResourceKind(to);
    if (fromKind === 'tgw' && toKind === 'tgw_attachment') tgwByAttachmentId.set(to.id, from.id);
    else if (toKind === 'tgw' && fromKind === 'tgw_attachment') tgwByAttachmentId.set(from.id, to.id);
    if (fromKind === 'tgw' && toKind === 'vpn_connection') tgwByVpnConnectionId.set(to.id, from.id);
    else if (toKind === 'tgw' && fromKind === 'vpn_connection') tgwByVpnConnectionId.set(from.id, to.id);
    if (fromKind === 'tgw_attachment' && toKind === 'eni') attachmentByEniId.set(to.id, from.id);
    else if (toKind === 'tgw_attachment' && fromKind === 'eni') attachmentByEniId.set(from.id, to.id);
    if (fromKind === 'vpn_connection' && toKind === 'tunnel') vpnConnectionByTunnelId.set(to.id, from.id);
    else if (toKind === 'vpn_connection' && fromKind === 'tunnel') vpnConnectionByTunnelId.set(from.id, to.id);
    if (fromKind === 'subnet' && toKind === 'vpc') vpcBySubnetId.set(from.id, to.id);
    else if (toKind === 'subnet' && fromKind === 'vpc') vpcBySubnetId.set(to.id, from.id);
  });

  // Attachment-to-VPN can appear before its TGW containment edge, so resolve
  // the association only after collecting all returned cloud relationships.
  edges.forEach((edge) => {
    const relation = edgeRelation(edge);
    if (!isSupportRelation(relation) && relation !== 'connects' && relation !== 'connect') return;
    const from = nodesById.get(edge.fromId);
    const to = nodesById.get(edge.toId);
    if (!from || !to) return;
    const fromKind = cloudResourceKind(from);
    const toKind = cloudResourceKind(to);
    if (fromKind === 'tgw_attachment' && toKind === 'vpn_connection') {
      const tgwId = tgwByAttachmentId.get(from.id);
      if (tgwId) tgwByVpnConnectionId.set(to.id, tgwId);
    } else if (toKind === 'tgw_attachment' && fromKind === 'vpn_connection') {
      const tgwId = tgwByAttachmentId.get(to.id);
      if (tgwId) tgwByVpnConnectionId.set(from.id, tgwId);
    }
  });

  attachmentByEniId.forEach((attachmentId, eniId) => {
    const tgwId = tgwByAttachmentId.get(attachmentId);
    if (tgwId) directDeviceByNodeId.set(eniId, tgwId);
  });
  vpnConnectionByTunnelId.forEach((vpnConnectionId, tunnelId) => {
    const tgwId = tgwByVpnConnectionId.get(vpnConnectionId);
    if (tgwId) directDeviceByNodeId.set(tunnelId, tgwId);
  });
  return { directDeviceByNodeId, tgwByVpnConnectionId, vpnConnectionByTunnelId, vpcBySubnetId };
};

const findEndpointAttachmentInterfaces = (
  nodesById: Map<string, TopologyNode>,
  edges: TopologyEdge[],
  directDeviceByNodeId: Map<string, string>
): Map<string, string> => {
  const interfaceIdsByEndpointId = new Map<string, Set<string>>();

  edges.forEach((edge) => {
    if (!isSupportRelation(edgeRelation(edge))) {
      return;
    }
    const from = nodesById.get(edge.fromId);
    const to = nodesById.get(edge.toId);
    if (!from || !to) {
      return;
    }
    const fromKind = nodeKind(from);
    const toKind = nodeKind(to);
    const endpointNode =
      fromKind === 'source' || fromKind === 'destination' || fromKind === 'endpoint'
        ? from
        : toKind === 'source' || toKind === 'destination' || toKind === 'endpoint'
          ? to
          : undefined;
    const interfaceNode = fromKind === 'interface' ? from : toKind === 'interface' ? to : undefined;
    if (!endpointNode || !interfaceNode || directDeviceByNodeId.has(interfaceNode.id)) {
      return;
    }
    if (!interfaceIdsByEndpointId.has(endpointNode.id)) {
      interfaceIdsByEndpointId.set(endpointNode.id, new Set());
    }
    interfaceIdsByEndpointId.get(endpointNode.id)?.add(interfaceNode.id);
  });

  const out = new Map<string, string>();
  interfaceIdsByEndpointId.forEach((interfaceIds, endpointId) => {
    if (interfaceIds.size === 1) {
      out.set(endpointId, Array.from(interfaceIds)[0] ?? '');
    }
  });
  return out;
};

type CloudEndpointOwnerPresentation = {
  interfaceIdByEndpointNodeId: Map<string, string>;
  terminalInterfaceIdByPathId: Map<string, string>;
  hiddenTailNodeIds: Set<string>;
};

// Cloud endpoint nodes are request/result markers. When the returned graph
// says their ENI belongs to an owner card, render the ENI chip in that owner
// instead of a second endpoint card. This consumes only endpoint bindings,
// containment, and the already-selected hop list.
const cloudEndpointOwnerPresentation = (
  graph: TopologyGraph,
  nodesById: Map<string, TopologyNode>,
  directDeviceByNodeId: Map<string, string>,
  selectedPathId: string | 'all'
): CloudEndpointOwnerPresentation => {
  const interfaceIdByEndpointNodeId = new Map<string, string>();
  graph.edges.forEach((edge) => {
    if (!isSupportRelation(edgeRelation(edge))) return;
    const from = nodesById.get(edge.fromId);
    const to = nodesById.get(edge.toId);
    if (!from || !to) return;
    const endpoint = ['source', 'destination', 'endpoint'].includes(nodeKind(from))
      ? from
      : ['source', 'destination', 'endpoint'].includes(nodeKind(to))
        ? to
        : undefined;
    const intf = nodeKind(from) === 'interface' ? from : nodeKind(to) === 'interface' ? to : undefined;
    if (!endpoint || !intf || cloudResourceKind(intf) !== 'eni' || !directDeviceByNodeId.has(intf.id)) return;
    interfaceIdByEndpointNodeId.set(endpoint.id, intf.id);
  });

  const pathMeta = graphPathMetadata(asRecord(graph.meta));
  const endpointNodeIdByRoleAndId = new Map<string, string>();
  graph.nodes.forEach((node) => {
    const role = nodeTraceRole(node);
    const endpointId = stringValue(getProperties(node).endpointId);
    if (role && endpointId) endpointNodeIdByRoleAndId.set(`${role}:${endpointId}`, node.id);
  });
  const terminalInterfaceIdByPathId = new Map<string, string>();
  const retainedHopNodeIds = new Set<string>();
  const tailNodeIds = new Set<string>();
  if (pathMeta && Array.isArray(pathMeta.paths)) {
    pathMeta.paths.forEach((rawPath, pathIndex) => {
      const path = asRecord(rawPath);
      if (!path) return;
      const pathId = stringValue(path.pathId ?? path.id) ?? `path:${pathIndex}`;
      if (selectedPathId !== 'all' && pathId !== selectedPathId) return;
      const hopNodeIds = (Array.isArray(path.hops) ? path.hops : [])
        .map(asRecord)
        .map((hop) => topologyRefId(hop?.node))
        .filter((nodeId): nodeId is string => Boolean(nodeId));
      const sourceEndpointId = topologyRefId(path.source);
      const sourceEndpointNodeId = sourceEndpointId
        ? endpointNodeIdByRoleAndId.get(`source:${sourceEndpointId}`)
        : undefined;
      const firstHopNodeId = hopNodeIds[0];
      const firstHop = firstHopNodeId ? nodesById.get(firstHopNodeId) : undefined;
      if (sourceEndpointNodeId && firstHop && cloudResourceKind(firstHop) === 'eni' && directDeviceByNodeId.has(firstHop.id)) {
        interfaceIdByEndpointNodeId.set(sourceEndpointNodeId, firstHop.id);
      }

      const destinationEndpointId = topologyRefId(path.destination);
      const destinationEndpointNodeId = destinationEndpointId
        ? endpointNodeIdByRoleAndId.get(`destination:${destinationEndpointId}`)
        : undefined;
      const lastHopNodeId = hopNodeIds.at(-1);
      const lastHop = lastHopNodeId ? nodesById.get(lastHopNodeId) : undefined;
      if (
        destinationEndpointNodeId &&
        !interfaceIdByEndpointNodeId.has(destinationEndpointNodeId) &&
        lastHop &&
        cloudResourceKind(lastHop) === 'eni' &&
        directDeviceByNodeId.has(lastHop.id)
      ) {
        interfaceIdByEndpointNodeId.set(destinationEndpointNodeId, lastHop.id);
      }
      const terminalInterfaceId = destinationEndpointNodeId ? interfaceIdByEndpointNodeId.get(destinationEndpointNodeId) : undefined;
      const terminalIndex = terminalInterfaceId ? hopNodeIds.indexOf(terminalInterfaceId) : -1;
      if (terminalInterfaceId && terminalIndex >= 0) {
        terminalInterfaceIdByPathId.set(pathId, terminalInterfaceId);
        hopNodeIds.slice(0, terminalIndex + 1).forEach((nodeId) => retainedHopNodeIds.add(nodeId));
        hopNodeIds.slice(terminalIndex + 1).forEach((nodeId) => tailNodeIds.add(nodeId));
      } else {
        hopNodeIds.forEach((nodeId) => retainedHopNodeIds.add(nodeId));
      }
    });
  }
  return {
    interfaceIdByEndpointNodeId,
    terminalInterfaceIdByPathId,
    hiddenTailNodeIds: new Set(Array.from(tailNodeIds).filter((nodeId) => !retainedHopNodeIds.has(nodeId)))
  };
};

const findSubnetVlanFolds = (nodesById: Map<string, TopologyNode>, edges: TopologyEdge[]) => {
  const subnetByVlanId = new Map<string, string>();
  const vlanIdsBySubnetId = new Map<string, Set<string>>();
  const subnetIdsByVlanId = new Map<string, Set<string>>();
  const subnetIdsByInterfaceId = new Map<string, Set<string>>();
  const vlanIdsByInterfaceId = new Map<string, Set<string>>();
  const disqualifiedVlanIds = new Set<string>();

  edges.forEach((edge) => {
    const from = nodesById.get(edge.fromId);
    const to = nodesById.get(edge.toId);
    if (!from || !to) {
      return;
    }
    const fromKind = nodeKind(from);
    const toKind = nodeKind(to);
    const relation = edgeRelation(edge);
    if (isSupportRelation(relation)) {
      const interfaceNode = fromKind === 'interface' ? from : toKind === 'interface' ? to : undefined;
      const subnetNode = fromKind === 'subnet' ? from : toKind === 'subnet' ? to : undefined;
      const vlanNode = fromKind === 'vlan' ? from : toKind === 'vlan' ? to : undefined;
      if (interfaceNode && subnetNode) {
        if (!subnetIdsByInterfaceId.has(interfaceNode.id)) {
          subnetIdsByInterfaceId.set(interfaceNode.id, new Set());
        }
        subnetIdsByInterfaceId.get(interfaceNode.id)?.add(subnetNode.id);
      }
      if (interfaceNode && vlanNode) {
        if (!vlanIdsByInterfaceId.has(interfaceNode.id)) {
          vlanIdsByInterfaceId.set(interfaceNode.id, new Set());
        }
        vlanIdsByInterfaceId.get(interfaceNode.id)?.add(vlanNode.id);
      }
    }

    const vlan = fromKind === 'vlan' ? from : toKind === 'vlan' ? to : undefined;
    if (!vlan || nodeRole(vlan) === 'source' || nodeRole(vlan) === 'destination') {
      return;
    }

    const pathLikeRelation = relation.includes('forwarding') || relation === 'path';
    if (pathLikeRelation && pathIdsForEdge(edge).length > 0) {
      disqualifiedVlanIds.add(vlan.id);
      return;
    }

    const other = vlan.id === from.id ? to : from;
    const otherKind = nodeKind(other);
    if (otherKind === 'subnet') {
      if (!subnetIdsByVlanId.has(vlan.id)) {
        subnetIdsByVlanId.set(vlan.id, new Set());
      }
      subnetIdsByVlanId.get(vlan.id)?.add(other.id);
      return;
    }

    if (otherKind === 'endpoint' && isSupportRelation(relation)) {
      return;
    }

    if (otherKind !== 'interface' || !isSupportRelation(relation)) {
      disqualifiedVlanIds.add(vlan.id);
    }
  });

  vlanIdsByInterfaceId.forEach((vlanIds, interfaceId) => {
    const subnetIds = subnetIdsByInterfaceId.get(interfaceId);
    if (!subnetIds || subnetIds.size === 0) {
      return;
    }
    vlanIds.forEach((vlanId) => {
      if (!subnetIdsByVlanId.has(vlanId)) {
        subnetIdsByVlanId.set(vlanId, new Set());
      }
      subnetIds.forEach((subnetId) => subnetIdsByVlanId.get(vlanId)?.add(subnetId));
    });
  });

  subnetIdsByVlanId.forEach((subnetIds, vlanId) => {
    if (disqualifiedVlanIds.has(vlanId) || subnetIds.size !== 1) {
      return;
    }
    const subnetId = Array.from(subnetIds)[0];
    if (!subnetId) {
      return;
    }
    subnetByVlanId.set(vlanId, subnetId);
    if (!vlanIdsBySubnetId.has(subnetId)) {
      vlanIdsBySubnetId.set(subnetId, new Set());
    }
    vlanIdsBySubnetId.get(subnetId)?.add(vlanId);
  });

  vlanIdsByInterfaceId.forEach((vlanIds, interfaceId) => {
    vlanIds.forEach((vlanId) => {
      const subnetId = subnetByVlanId.get(vlanId);
      if (!subnetId) {
        return;
      }
      if (!subnetIdsByInterfaceId.has(interfaceId)) {
        subnetIdsByInterfaceId.set(interfaceId, new Set());
      }
      subnetIdsByInterfaceId.get(interfaceId)?.add(subnetId);
    });
  });

  return { subnetByVlanId, vlanIdsBySubnetId, subnetIdsByInterfaceId, vlanIdsByInterfaceId };
};

const pathFlowCardEdgeKey = (edge: PathFlowCardEdge): string => `${edge.fromCardId}:${edge.toCardId}:${edge.pathIds.join(',')}`;

const cardTraceRole = (card: Pick<PathCard, 'kind' | 'role'> | Omit<PathCard, 'x' | 'y' | 'rank'>): EndpointRole | undefined => {
  const role = normalized(card.role);
  if (role === 'source' || role === 'destination') {
    return role;
  }
  if (card.kind === 'source' || card.kind === 'destination') {
    return card.kind;
  }
  return undefined;
};

const pathFlowCardEdgeEndpointScore = (
  nodeId: string,
  cardId: string,
  nodesById: Map<string, TopologyNode>,
  nodeToCardId: Record<string, string>
): number => {
  const node = nodesById.get(nodeId);
  if (!node) {
    return Number.MAX_SAFE_INTEGER;
  }
  const foldedIntoAnotherCard = Boolean(nodeToCardId[nodeId]) && nodeToCardId[nodeId] !== nodeId;
  const traceRole = nodeTraceRole(node);
  const baseScore =
    nodeKind(node) === 'device'
      ? 0
      : nodeKind(node) === 'subnet' || traceRole === 'source' || traceRole === 'destination'
        ? 1
        : nodeKind(node) === 'interface'
          ? 2
          : nodeKind(node) === 'vlan'
            ? 8
            : 4;
  return baseScore + (foldedIntoAnotherCard ? 12 : 0) + (nodeId === cardId ? -1 : 0);
};

const comparePathFlowCardEdgePriority = (
  left: PathFlowCardEdge,
  right: PathFlowCardEdge,
  nodesById: Map<string, TopologyNode>,
  nodeToCardId: Record<string, string>
): number => {
  const leftScore =
    pathFlowCardEdgeEndpointScore(left.fromNodeId, left.fromCardId, nodesById, nodeToCardId) +
    pathFlowCardEdgeEndpointScore(left.toNodeId, left.toCardId, nodesById, nodeToCardId);
  const rightScore =
    pathFlowCardEdgeEndpointScore(right.fromNodeId, right.fromCardId, nodesById, nodeToCardId) +
    pathFlowCardEdgeEndpointScore(right.toNodeId, right.toCardId, nodesById, nodeToCardId);
  if (leftScore !== rightScore) {
    return leftScore - rightScore;
  }
  if (Boolean(left.swapped) !== Boolean(right.swapped)) {
    return left.swapped ? 1 : -1;
  }
  if (isPresentationSequenceEdge(left.edge) !== isPresentationSequenceEdge(right.edge)) {
    return isPresentationSequenceEdge(left.edge) ? -1 : 1;
  }
  return left.edge.id.localeCompare(right.edge.id);
};

const dedupePathFlowCardEdges = (
  edges: PathFlowCardEdge[],
  nodesById: Map<string, TopologyNode>,
  nodeToCardId: Record<string, string>
): { edges: PathFlowCardEdge[]; keptEdgeIds: Set<string> } => {
  const bestByKey = new Map<string, PathFlowCardEdge>();
  edges.forEach((edge) => {
    const key = pathFlowCardEdgeKey(edge);
    const current = bestByKey.get(key);
    if (!current || comparePathFlowCardEdgePriority(edge, current, nodesById, nodeToCardId) < 0) {
      bestByKey.set(key, edge);
    }
  });
  const deduped = Array.from(bestByKey.values());
  return {
    edges: deduped,
    keptEdgeIds: new Set(deduped.map((edge) => edge.edge.id))
  };
};

const rankCards = (
  cards: Array<Omit<PathCard, 'x' | 'y' | 'rank'>>,
  cardEdges: Array<{ fromCardId: string; toCardId: string }>
): Map<string, number> => {
  const sourceCardIds = cards.filter((card) => cardTraceRole(card) === 'source').map((card) => card.id);
  const adjacency = new Map<string, Set<string>>();
  cards.forEach((card) => adjacency.set(card.id, new Set()));
  cardEdges.forEach((edge) => adjacency.get(edge.fromCardId)?.add(edge.toCardId));
  const indegree = new Map(cards.map((card) => [card.id, 0]));
  const outdegree = new Map(cards.map((card) => [card.id, 0]));
  cardEdges.forEach((edge) => {
    indegree.set(edge.toCardId, (indegree.get(edge.toCardId) ?? 0) + 1);
    outdegree.set(edge.fromCardId, (outdegree.get(edge.fromCardId) ?? 0) + 1);
  });
  const headCardIds = cards
    .filter((card) => (indegree.get(card.id) ?? 0) === 0 && (outdegree.get(card.id) ?? 0) > 0)
    .map((card) => card.id);
  const starts = sourceCardIds.length > 0 ? sourceCardIds : headCardIds.length > 0 ? headCardIds : cards.slice(0, 1).map((card) => card.id);
  const startIds = new Set(starts);

  const reaches = (fromId: string, targetId: string, visited = new Set<string>()): boolean => {
    if (fromId === targetId) {
      return true;
    }
    if (visited.has(fromId)) {
      return false;
    }
    visited.add(fromId);
    return Array.from(adjacency.get(fromId) ?? []).some((nextId) => reaches(nextId, targetId, visited));
  };
  const acyclicCardEdges = cardEdges.filter((edge) => !reaches(edge.toCardId, edge.fromCardId));
  const ranks = new Map<string, number>();
  starts.forEach((id) => ranks.set(id, 0));

  for (let iteration = 0; iteration < cards.length; iteration += 1) {
    let changed = false;
    acyclicCardEdges.forEach((edge) => {
      if (startIds.has(edge.toCardId)) {
        return;
      }
      const fromRank = ranks.get(edge.fromCardId);
      if (fromRank === undefined) {
        return;
      }
      const nextRank = fromRank + 1;
      if (nextRank > (ranks.get(edge.toCardId) ?? -1)) {
        ranks.set(edge.toCardId, nextRank);
        changed = true;
      }
    });
    if (!changed) {
      break;
    }
  }

  const fallbackRankByKind: Record<CardKind, number> = {
    source: 0,
    subnet: 1,
    vlan: 1,
    device: 2,
    vrf: 2,
    endpoint: 3,
    domain: 3,
    other: 3,
    destination: 4
  };
  cards.forEach((card) => {
    if (!ranks.has(card.id)) {
      ranks.set(card.id, fallbackRankByKind[card.kind] ?? 3);
    }
  });

  const maxRank = Math.max(0, ...Array.from(ranks.values()));
  cards
    .filter((card) => cardTraceRole(card) === 'destination')
    .forEach((card) => {
      ranks.set(card.id, Math.max(ranks.get(card.id) ?? 0, maxRank));
    });

  return ranks;
};

const cardLane = (card: Omit<PathCard, 'x' | 'y' | 'rank'>): number => {
  if (cardTraceRole(card) === 'source' || cardTraceRole(card) === 'destination') {
    return 0;
  }
  switch (card.kind) {
    case 'source':
      return 0;
    case 'subnet':
    case 'vlan':
      return 1;
    case 'device':
      return 2;
    case 'vrf':
      return 3;
    case 'endpoint':
      return 4;
    case 'domain':
      return 4;
    case 'destination':
      return 0;
    default:
      return 5;
  }
};

const cardSize = (
  kind: CardKind,
  vrfGroups: PathCard['vrfGroups'],
  vpnGroups: PathCard['vpnGroups'],
  interfaces: TopologyNode[],
  interfaceLayoutById: PathInterfaceLayoutById
) => {
  if (kind === 'subnet') {
    return { width: SUBNET_CARD_SIZE, height: SUBNET_CARD_SIZE };
  }
  if (kind !== 'device') {
    return { width: BASE_CARD_WIDTH, height: kind === 'source' || kind === 'destination' ? 128 : BASE_CARD_HEIGHT };
  }
  const vrfHeight = [...vrfGroups, ...vpnGroups].reduce((sum, group) => sum + vrfGroupHeight(group), 0);
  const interfaceHeight = ungroupedInterfaceHeight(interfaces, interfaceLayoutById);
  const embeddedSectionCount = vrfGroups.length + vpnGroups.length + (interfaces.length > 0 ? 1 : 0);
  const embeddedHeight = vrfHeight + interfaceHeight + Math.max(0, embeddedSectionCount - 1) * DEVICE_SECTION_GAP;
  return { width: DEVICE_WIDTH, height: DEVICE_HEADER_HEIGHT + Math.max(54, embeddedHeight) };
};

const interfaceRelatedRank = (
  node: TopologyNode,
  rolesById: Map<string, EndpointRole[]>,
  nodeToCardId: Record<string, string>,
  ranks: Map<string, number>,
  subnetIdsByInterfaceId: Map<string, Set<string>>
): number => {
  const roles = rolesById.get(node.id) ?? [];
  const hasSource = roles.includes('source');
  const hasDestination = roles.includes('destination');
  const relatedRanks = Array.from(subnetIdsByInterfaceId.get(node.id) ?? [])
    .map((subnetId) => ranks.get(nodeToCardId[subnetId] ?? subnetId))
    .filter((rank): rank is number => typeof rank === 'number')
    .sort((left, right) => left - right);
  if (relatedRanks.length > 0) {
    return relatedRanks[0] ?? 0;
  }
  if (hasSource && !hasDestination) {
    return -1;
  }
  if (hasDestination && !hasSource) {
    return Number.MAX_SAFE_INTEGER;
  }
  if (hasSource && hasDestination) {
    return 0;
  }
  return Number.MAX_SAFE_INTEGER - 1;
};

const interfaceLayoutColumn = (
  node: TopologyNode,
  rolesById: Map<string, EndpointRole[]>,
  nodeToCardId: Record<string, string>,
  ranks: Map<string, number>,
  subnetIdsByInterfaceId: Map<string, Set<string>>,
  interfaceFlowRolesByPath: InterfaceFlowRolesByPath,
  selectedPathId: string | 'all',
  flowRoleOverrides?: InterfaceFlowRoleOverrides
): number => {
  const forcedFlowRole = flowRoleOverrides?.get(node.id);
  const flowRoles = forcedFlowRole
    ? new Set<PathInterfaceFlowRole>([forcedFlowRole])
    : interfaceFlowRolesForSelection(node.id, interfaceFlowRolesByPath, selectedPathId);
  if (flowRoles.has('ingress') && !flowRoles.has('egress')) {
    return 0;
  }
  if (flowRoles.has('egress') && !flowRoles.has('ingress')) {
    return 2;
  }
  const roles = rolesById.get(node.id) ?? [];
  const hasSource = roles.includes('source');
  const hasDestination = roles.includes('destination');
  if (hasSource && !hasDestination) {
    return 0;
  }
  if (hasDestination && !hasSource) {
    return 2;
  }
  const cardRank = ranks.get(nodeToCardId[node.id] ?? node.id);
  if (typeof cardRank === 'number') {
    const relatedRanks = Array.from(subnetIdsByInterfaceId.get(node.id) ?? [])
      .map((subnetId) => ranks.get(nodeToCardId[subnetId] ?? subnetId))
      .filter((rank): rank is number => typeof rank === 'number');
    const hasLeftSubnet = relatedRanks.some((rank) => rank < cardRank);
    const hasRightSubnet = relatedRanks.some((rank) => rank > cardRank);
    if (hasLeftSubnet && !hasRightSubnet) {
      return 0;
    }
    if (hasRightSubnet && !hasLeftSubnet) {
      return 2;
    }
  }
  return 1;
};

const compareDeviceInterfacesWithinColumn = (
  left: TopologyNode,
  right: TopologyNode,
  rolesById: Map<string, EndpointRole[]>,
  nodeToCardId: Record<string, string>,
  ranks: Map<string, number>,
  subnetIdsByInterfaceId: Map<string, Set<string>>
): number => {
  const leftRank = interfaceRelatedRank(left, rolesById, nodeToCardId, ranks, subnetIdsByInterfaceId);
  const rightRank = interfaceRelatedRank(right, rolesById, nodeToCardId, ranks, subnetIdsByInterfaceId);
  if (leftRank !== rightRank) {
    return leftRank - rightRank;
  }
  return compareInterfacesForDisplay(left, right, rolesById);
};

const layoutDeviceInterfacesForPath = (
  interfaces: TopologyNode[],
  rolesById: Map<string, EndpointRole[]>,
  nodeToCardId: Record<string, string>,
  ranks: Map<string, number>,
  subnetIdsByInterfaceId: Map<string, Set<string>>,
  interfaceFlowRolesByPath: InterfaceFlowRolesByPath,
  selectedPathId: string | 'all',
  flowRoleOverrides?: InterfaceFlowRoleOverrides
): { interfaces: TopologyNode[]; interfaceLayoutById: PathInterfaceLayoutById } => {
  const interfacesByColumn = [[], [], []] as TopologyNode[][];
  interfaces.forEach((intf) => {
    interfacesByColumn[
      interfaceLayoutColumn(
        intf,
        rolesById,
        nodeToCardId,
        ranks,
        subnetIdsByInterfaceId,
        interfaceFlowRolesByPath,
        selectedPathId,
        flowRoleOverrides
      )
    ]?.push(intf);
  });
  interfacesByColumn.forEach((columnInterfaces) =>
    columnInterfaces.sort((left, right) => compareDeviceInterfacesWithinColumn(left, right, rolesById, nodeToCardId, ranks, subnetIdsByInterfaceId))
  );
  const interfaceLayoutById: PathInterfaceLayoutById = {};
  interfacesByColumn.forEach((columnInterfaces, column) => {
    columnInterfaces.forEach((intf, row) => {
      interfaceLayoutById[intf.id] = { column, row };
    });
  });
  return {
    interfaces: interfacesByColumn.flat(),
    interfaceLayoutById
  };
};

const deviceGroupedInterfaceAnchor = (card: PathCard, y: number, slot: PathInterfaceSlot): Point => {
  const { column, row } = slot;
  const left = card.x + DEVICE_CARD_PADDING + VRF_GROUP_PADDING;
  const gridWidth = card.width - DEVICE_CARD_PADDING * 2 - VRF_GROUP_PADDING * 2;
  return {
    x: interfacePortColumnX(left, gridWidth, column),
    y: y + VRF_GROUP_PADDING + VRF_GROUP_TITLE_HEIGHT + VRF_GROUP_TITLE_GAP + INTERFACE_CHIP_HEIGHT / 2 + row * INTERFACE_ROW_HEIGHT
  };
};

const deviceUngroupedInterfaceAnchor = (card: PathCard, y: number, slot: PathInterfaceSlot): Point => {
  const { column, row } = slot;
  const left = card.x + DEVICE_CARD_PADDING;
  const gridWidth = card.width - DEVICE_CARD_PADDING * 2;
  return {
    x: interfacePortColumnX(left, gridWidth, column),
    y: y + UNGROUPED_INTERFACE_TOP_OFFSET + INTERFACE_CHIP_HEIGHT / 2 + row * INTERFACE_ROW_HEIGHT
  };
};

const deviceInterfaceAnchorPoint = (card: PathCard, nodeId: string): Point | undefined => {
  if (card.kind !== 'device') {
    return undefined;
  }
  let y = card.y + DEVICE_HEADER_HEIGHT;
  for (const group of [...card.vrfGroups, ...card.vpnGroups]) {
    const groupHeight = vrfGroupHeight(group);
    const interfaceIndex = group.interfaces.findIndex((intf) => intf.id === nodeId);
    if (interfaceIndex >= 0) {
      return deviceGroupedInterfaceAnchor(card, y, group.interfaceLayoutById[nodeId] ?? defaultInterfaceSlotForIndex(interfaceIndex));
    }
    y += groupHeight + DEVICE_SECTION_GAP;
  }

  const interfaceIndex = card.interfaces.findIndex((intf) => intf.id === nodeId);
  if (interfaceIndex >= 0) {
    return deviceUngroupedInterfaceAnchor(card, y, card.interfaceLayoutById[nodeId] ?? defaultInterfaceSlotForIndex(interfaceIndex));
  }
  return undefined;
};

const deviceChildAnchor = (card: PathCard, nodeId: string, other: PathCard): PathRouteAnchor | undefined => {
  if (card.kind !== 'device') {
    return undefined;
  }
  const point = deviceInterfaceAnchorPoint(card, nodeId);
  const side: RectSide = other.x + other.width / 2 >= card.x + card.width / 2 ? 'right' : 'left';
  if (point) {
    return {
      ...point,
      side,
      portNodeId: nodeId
    };
  }
  let y = card.y + DEVICE_HEADER_HEIGHT;
  for (const group of [...card.vrfGroups, ...card.vpnGroups]) {
    const groupHeight = vrfGroupHeight(group);
    if (group.node.id === nodeId) {
      return {
        x: card.x + card.width / 2,
        y: y + groupHeight / 2,
        side
      };
    }
    y += groupHeight + DEVICE_SECTION_GAP;
  }
  return undefined;
};

const edgeEndpointForNode = (nodeId: string, card: PathCard, other: PathCard): PathRouteAnchor =>
  deviceChildAnchor(card, nodeId, other) ?? edgeEndpoint(card, other);

const allDeviceInterfaces = (card: PathCard): TopologyNode[] =>
  card.kind === 'device' ? [...card.vrfGroups.flatMap((group) => group.interfaces), ...card.vpnGroups.flatMap((group) => group.interfaces), ...card.interfaces] : [];

const firstDeviceInterfaceAnchor = (card: PathCard, other: PathCard): PathRouteAnchor | undefined => {
  const firstInterface = allDeviceInterfaces(card)[0];
  return firstInterface ? deviceChildAnchor(card, firstInterface.id, other) : undefined;
};

const pathIdsOverlap = (left: string[], right: string[]): boolean => left.some((id) => right.includes(id));

const vlanIdentityNumber = (value: string): string | undefined => {
  const trimmed = value.trim();
  return (
    trimmed.match(/(?:^|[^a-z0-9])vlan[\s._:-]*(\d+)(?=$|[^a-z0-9])/i)?.[1] ??
    trimmed.match(/(?:^|[^a-z0-9])vi[\s._:-]*(\d+)(?=$|[^a-z0-9])/i)?.[1] ??
    trimmed.match(/^\d+$/)?.[0]
  );
};

const vlanIdentityKeys = (node: TopologyNode): string[] => {
  const labels = getLabels(node);
  const properties = getProperties(node);
  const rawValues = [
    node.id,
    displayName(node),
    stringValue(labels.vlan),
    stringValue(properties.vlan),
    stringValue(labels.vlanId),
    stringValue(properties.vlanId),
    stringValue(labels.vlan_id),
    stringValue(properties.vlan_id)
  ].filter((value): value is string => Boolean(value));
  return unique(
    rawValues.flatMap((raw) => {
      const value = raw.trim();
      const normalizedValue = normalized(value);
      const numeric = vlanIdentityNumber(value);
      return numeric ? [normalizedValue, `vlan:${numeric}`] : [normalizedValue];
    })
  );
};

const vlanIdentityMatches = (left: TopologyNode, right: TopologyNode): boolean => {
  const rightKeys = new Set(vlanIdentityKeys(right));
  return vlanIdentityKeys(left).some((key) => rightKeys.has(key));
};

const subnetInterfaceAnchor = (
  card: PathCard,
  subnet: PathCard,
  edgePathIds: string[],
  subnetIdsByInterfaceId: Map<string, Set<string>>,
  vlanIdsByInterfaceId: Map<string, Set<string>>
): PathRouteAnchor | undefined => {
  const matchingInterfaces = allDeviceInterfaces(card).filter((intf) => subnetIdsByInterfaceId.get(intf.id)?.has(subnet.id));
  if (matchingInterfaces.length === 0) {
    return undefined;
  }
  const subnetVlanIds = new Set(subnet.vlanChips.map((vlan) => vlan.id));
  const vlanMatchedInterfaces =
    subnet.vlanChips.length > 0
      ? matchingInterfaces.filter((intf) => {
          const interfaceVlanIds = vlanIdsByInterfaceId.get(intf.id);
          return (
            (interfaceVlanIds && Array.from(interfaceVlanIds).some((vlanId) => subnetVlanIds.has(vlanId))) ||
            subnet.vlanChips.some((vlan) => vlanIdentityMatches(intf, vlan))
          );
        })
      : [];
  const pathMatch = (intf: TopologyNode) => {
    const interfacePathIds = pathIdsForNode(intf);
    return interfacePathIds.length > 0 && pathIdsOverlap(interfacePathIds, edgePathIds);
  };
  const pathMatchedInterface =
    edgePathIds.length > 0
      ? matchingInterfaces.find(pathMatch)
      : undefined;
  const untagged = (intf: TopologyNode) => pathIdsForNode(intf).length === 0;
  const selectedInterface =
    (edgePathIds.length > 0 ? vlanMatchedInterfaces.find(pathMatch) : undefined) ??
    vlanMatchedInterfaces.find(untagged) ??
    vlanMatchedInterfaces[0] ??
    pathMatchedInterface ??
    matchingInterfaces.find(untagged) ??
    matchingInterfaces[0];
  return deviceChildAnchor(card, selectedInterface.id, subnet);
};

const bestEdgeEndpointForNode = (
  nodeId: string,
  card: PathCard,
  other: PathCard,
  edgePathIds: string[],
  subnetIdsByInterfaceId: Map<string, Set<string>>,
  vlanIdsByInterfaceId: Map<string, Set<string>>
): PathRouteAnchor => {
  if (card.kind === 'device' && nodeId === card.id && other.kind === 'subnet') {
    return (
      subnetInterfaceAnchor(card, other, edgePathIds, subnetIdsByInterfaceId, vlanIdsByInterfaceId) ??
      firstDeviceInterfaceAnchor(card, other) ??
      edgeEndpoint(card, other)
    );
  }
  return edgeEndpointForNode(nodeId, card, other);
};

const circularEdgeEndpoint = (card: PathCard, other: PathCard): PathRouteAnchor => {
  const center = { x: card.x + card.width / 2, y: card.y + card.height / 2 };
  const otherCenter = { x: other.x + other.width / 2, y: other.y + other.height / 2 };
  const dx = otherCenter.x - center.x;
  const dy = otherCenter.y - center.y;
  const radiusX = card.width / 2;
  const radiusY = card.height / 2;
  if (Math.abs(dx) < ROUTE_EPSILON && Math.abs(dy) < ROUTE_EPSILON) {
    return { x: center.x + radiusX, y: center.y, side: 'right' };
  }

  const scale = 1 / Math.sqrt((dx * dx) / (radiusX * radiusX) + (dy * dy) / (radiusY * radiusY));
  const x = center.x + dx * scale;
  const y = center.y + dy * scale;
  const normalLength = Math.hypot(x - center.x, y - center.y) || 1;
  const side: RectSide =
    Math.abs((x - center.x) / radiusX) >= Math.abs((y - center.y) / radiusY)
      ? dx >= 0
        ? 'right'
        : 'left'
      : dy >= 0
        ? 'bottom'
        : 'top';
  return {
    x,
    y,
    side,
    normal: {
      x: (x - center.x) / normalLength,
      y: (y - center.y) / normalLength
    }
  };
};

const edgeEndpoint = (card: PathCard, other: PathCard): PathRouteAnchor => {
  if (card.kind === 'subnet') {
    return circularEdgeEndpoint(card, other);
  }
  const center = { x: card.x + card.width / 2, y: card.y + card.height / 2 };
  const otherCenter = { x: other.x + other.width / 2, y: other.y + other.height / 2 };
  const dx = otherCenter.x - center.x;
  const dy = otherCenter.y - center.y;
  if (Math.abs(dx) < ROUTE_EPSILON && Math.abs(dy) < ROUTE_EPSILON) {
    return { x: card.x + card.width, y: center.y, side: 'right' };
  }

  const halfWidth = card.width / 2;
  const halfHeight = card.height / 2;
  const scaleX = Math.abs(dx) < ROUTE_EPSILON ? Number.POSITIVE_INFINITY : halfWidth / Math.abs(dx);
  const scaleY = Math.abs(dy) < ROUTE_EPSILON ? Number.POSITIVE_INFINITY : halfHeight / Math.abs(dy);
  if (scaleX < scaleY) {
    return {
      x: dx > 0 ? card.x + card.width : card.x,
      y: center.y + dy * scaleX,
      side: dx > 0 ? 'right' : 'left'
    };
  }
  return {
    x: center.x + dx * scaleY,
    y: dy > 0 ? card.y + card.height : card.y,
    side: dy > 0 ? 'bottom' : 'top'
  };
};

const normalForSide = (side: RectSide): Point => {
  switch (side) {
    case 'left':
      return { x: -1, y: 0 };
    case 'right':
      return { x: 1, y: 0 };
    case 'top':
      return { x: 0, y: -1 };
    case 'bottom':
      return { x: 0, y: 1 };
  }
};

const outwardPoint = (anchor: PathRouteAnchor, distance = EDGE_EXIT_PADDING): Point => {
  const normal = anchor.normal ?? normalForSide(anchor.side);
  return { x: anchor.x + normal.x * distance, y: anchor.y + normal.y * distance };
};

const cardRouteRect = (card: PathCard, clearance = EDGE_RECT_CLEARANCE): PathRouteRect => ({
  id: card.id,
  left: card.x - clearance,
  right: card.x + card.width + clearance,
  top: card.y - clearance,
  bottom: card.y + card.height + clearance
});

const dedupeRoutePoints = (points: Point[]): Point[] => {
  const out: Point[] = [];
  points.forEach((point) => {
    const prev = out[out.length - 1];
    if (!prev || Math.abs(prev.x - point.x) > ROUTE_EPSILON || Math.abs(prev.y - point.y) > ROUTE_EPSILON) {
      out.push(point);
    }
  });
  return out;
};

const pointInsideRect = (point: Point, rect: PathRouteRect): boolean =>
  point.x > rect.left + ROUTE_EPSILON &&
  point.x < rect.right - ROUTE_EPSILON &&
  point.y > rect.top + ROUTE_EPSILON &&
  point.y < rect.bottom - ROUTE_EPSILON;

const segmentIntersectsRect = (from: Point, to: Point, rect: PathRouteRect): boolean => {
  if (Math.abs(from.x - to.x) <= ROUTE_EPSILON) {
    const x = from.x;
    const minY = Math.min(from.y, to.y);
    const maxY = Math.max(from.y, to.y);
    return x > rect.left + ROUTE_EPSILON && x < rect.right - ROUTE_EPSILON && minY < rect.bottom - ROUTE_EPSILON && maxY > rect.top + ROUTE_EPSILON;
  }
  if (Math.abs(from.y - to.y) <= ROUTE_EPSILON) {
    const y = from.y;
    const minX = Math.min(from.x, to.x);
    const maxX = Math.max(from.x, to.x);
    return y > rect.top + ROUTE_EPSILON && y < rect.bottom - ROUTE_EPSILON && minX < rect.right - ROUTE_EPSILON && maxX > rect.left + ROUTE_EPSILON;
  }
  return true;
};

const routeIsClear = (points: Point[], rects: PathRouteRect[]): boolean =>
  points.every((point) => !rects.some((rect) => pointInsideRect(point, rect))) &&
  points.slice(1).every((point, index) => !rects.some((rect) => segmentIntersectsRect(points[index], point, rect)));

const sortedNumbers = (values: number[]): number[] =>
  Array.from(new Set(values)).sort((left, right) => left - right);

const pointKey = (point: Point): string => `${point.x},${point.y}`;

const parsePointKey = (key: string): Point => {
  const [x, y] = key.split(',').map(Number);
  return { x, y };
};

const directOrthogonalRoute = (from: PathRouteAnchor, to: PathRouteAnchor): Point[] => {
  const start = outwardPoint(from);
  const end = outwardPoint(to);
  const fromHorizontal = from.side === 'left' || from.side === 'right';
  const toHorizontal = to.side === 'left' || to.side === 'right';
  const points: Point[] = [start];

  if (fromHorizontal && toHorizontal) {
    const midX = (start.x + end.x) / 2;
    points.push({ x: midX, y: start.y }, { x: midX, y: end.y });
  } else if (!fromHorizontal && !toHorizontal) {
    const midY = (start.y + end.y) / 2;
    points.push({ x: start.x, y: midY }, { x: end.x, y: midY });
  } else if (fromHorizontal) {
    points.push({ x: start.x, y: end.y });
  } else {
    points.push({ x: end.x, y: start.y });
  }

  points.push(end);
  return dedupeRoutePoints(points);
};

export const buildRectAvoidingPathRoute = (
  from: PathRouteAnchor,
  to: PathRouteAnchor,
  rects: PathRouteRect[]
): Point[] => {
  const direct = directOrthogonalRoute(from, to);
  if (routeIsClear(direct, rects)) {
    return dedupeRoutePoints([from, ...direct, to]);
  }

  const start = direct[0];
  const end = direct[direct.length - 1];
  const minX = Math.min(start.x, end.x, ...rects.map((rect) => rect.left)) - EDGE_EXIT_PADDING;
  const maxX = Math.max(start.x, end.x, ...rects.map((rect) => rect.right)) + EDGE_EXIT_PADDING;
  const minY = Math.min(start.y, end.y, ...rects.map((rect) => rect.top)) - EDGE_EXIT_PADDING;
  const maxY = Math.max(start.y, end.y, ...rects.map((rect) => rect.bottom)) + EDGE_EXIT_PADDING;
  const xs = sortedNumbers([
    minX,
    maxX,
    start.x,
    end.x,
    ...rects.flatMap((rect) => [rect.left - EDGE_RECT_CLEARANCE, rect.right + EDGE_RECT_CLEARANCE])
  ]);
  const ys = sortedNumbers([
    minY,
    maxY,
    start.y,
    end.y,
    ...rects.flatMap((rect) => [rect.top - EDGE_RECT_CLEARANCE, rect.bottom + EDGE_RECT_CLEARANCE])
  ]);
  const startKey = pointKey(start);
  const endKey = pointKey(end);
  const distanceByKey = new Map<string, number>([[startKey, 0]]);
  const previousByKey = new Map<string, string>();
  const open = new Set<string>([startKey]);
  const visited = new Set<string>();

  const isPointAllowed = (point: Point): boolean => !rects.some((rect) => pointInsideRect(point, rect));
  const isSegmentAllowed = (left: Point, right: Point): boolean =>
    isPointAllowed(right) && !rects.some((rect) => segmentIntersectsRect(left, right, rect));

  while (open.size > 0) {
    let currentKey = '';
    let currentScore = Number.POSITIVE_INFINITY;
    open.forEach((key) => {
      const point = parsePointKey(key);
      const distance = distanceByKey.get(key) ?? Number.POSITIVE_INFINITY;
      const score = distance + Math.abs(point.x - end.x) + Math.abs(point.y - end.y);
      if (score < currentScore) {
        currentScore = score;
        currentKey = key;
      }
    });
    if (!currentKey) {
      break;
    }
    if (currentKey === endKey) {
      break;
    }
    open.delete(currentKey);
    visited.add(currentKey);

    const current = parsePointKey(currentKey);
    const xIndex = xs.indexOf(current.x);
    const yIndex = ys.indexOf(current.y);
    const neighbors = [
      xIndex > 0 ? { x: xs[xIndex - 1], y: current.y } : undefined,
      xIndex < xs.length - 1 ? { x: xs[xIndex + 1], y: current.y } : undefined,
      yIndex > 0 ? { x: current.x, y: ys[yIndex - 1] } : undefined,
      yIndex < ys.length - 1 ? { x: current.x, y: ys[yIndex + 1] } : undefined
    ].filter((point): point is Point => Boolean(point));

    neighbors.forEach((neighbor) => {
      const neighborKey = pointKey(neighbor);
      if (visited.has(neighborKey) || !isSegmentAllowed(current, neighbor)) {
        return;
      }
      const nextDistance = (distanceByKey.get(currentKey) ?? 0) + Math.abs(current.x - neighbor.x) + Math.abs(current.y - neighbor.y);
      if (nextDistance < (distanceByKey.get(neighborKey) ?? Number.POSITIVE_INFINITY)) {
        distanceByKey.set(neighborKey, nextDistance);
        previousByKey.set(neighborKey, currentKey);
        open.add(neighborKey);
      }
    });
  }

  if (!previousByKey.has(endKey)) {
    return dedupeRoutePoints([from, ...direct, to]);
  }

  const innerKeys: string[] = [];
  let cursor = endKey;
  while (cursor) {
    innerKeys.push(cursor);
    if (cursor === startKey) {
      break;
    }
    cursor = previousByKey.get(cursor) ?? '';
  }

  return dedupeRoutePoints([from, ...innerKeys.reverse().map(parsePointKey), to]);
};

export const buildPathRelatedViewModel = (
  graph: TopologyGraph,
  selectedPathId: string | 'all' = 'all'
): PathRelatedViewModel => {
  const graphMeta = asRecord(graph.meta);
  const nodesById = new Map(graph.nodes.map((node) => [node.id, node]));
  const interfaceRolesById = interfaceEndpointRoleMap(graph, selectedPathId);
  const allPathIds = graphPathIds(graph);
  const pathIds = allPathIds.length > 0 ? allPathIds : ['path'];
  const { directDeviceByNodeId, vrfByInterfaceId } = findContainment(nodesById, graph.edges);
  const cloudContainment = findCloudPresentationContainment(nodesById, graph.edges);
  cloudContainment.directDeviceByNodeId.forEach((deviceId, nodeId) => directDeviceByNodeId.set(nodeId, deviceId));
  const { tgwByVpnConnectionId, vpnConnectionByTunnelId, vpcBySubnetId } = cloudContainment;
  const tunnelInterfaceAliasByNodeId = tunnelInterfaceAliasesFromReferences(nodesById);
  const endpointAttachmentInterfaceIdByEndpointId = findEndpointAttachmentInterfaces(nodesById, graph.edges, directDeviceByNodeId);
  const { subnetByVlanId, vlanIdsBySubnetId, subnetIdsByInterfaceId, vlanIdsByInterfaceId } = findSubnetVlanFolds(nodesById, graph.edges);
  const interfaceFlowRolesByPath = interfaceFlowRolesFromGraphMeta(graphMeta ?? {});
  const hasCloudResources = graph.nodes.some((node) => Boolean(cloudResourceKind(node)));
  const repeatedCloudStages = hasCloudResources
    ? repeatedCloudStagesFromGraphMeta(graphMeta ?? {}, selectedPathId, nodesById, directDeviceByNodeId)
    : { stagesByResourceId: new Map<string, RepeatedCloudStage[]>(), cardIdByPathHop: new Map<string, string>() };
  const endpointOwnerPresentation = hasCloudResources
    ? cloudEndpointOwnerPresentation(graph, nodesById, directDeviceByNodeId, selectedPathId)
    : { interfaceIdByEndpointNodeId: new Map<string, string>(), terminalInterfaceIdByPathId: new Map<string, string>(), hiddenTailNodeIds: new Set<string>() };
  endpointOwnerPresentation.interfaceIdByEndpointNodeId.forEach((interfaceId, endpointNodeId) => {
    const endpoint = nodesById.get(endpointNodeId);
    const role = endpoint ? nodeTraceRole(endpoint) : undefined;
    if (role !== 'source' && role !== 'destination') return;
    interfaceRolesById.set(interfaceId, unique([...(interfaceRolesById.get(interfaceId) ?? []), role]) as EndpointRole[]);
  });
  const orderedHopEdges = hasCloudResources
    ? cloudHopSequenceEdgesFromGraphMeta(
        graphMeta ?? {},
        selectedPathId,
        endpointOwnerPresentation.terminalInterfaceIdByPathId,
        nodesById,
        repeatedCloudStages.cardIdByPathHop
      )
    : [];
  const sequencePathIds = new Set(orderedHopEdges.flatMap(pathIdsForEdge));
  const flowSourceEdges = [...graph.edges, ...orderedHopEdges];
  const isCloudContextEdge = (edge: TopologyEdge): boolean => {
    const from = nodesById.get(edge.fromId);
    const to = nodesById.get(edge.toId);
    return Boolean((from && cloudResourceKind(from)) || (to && cloudResourceKind(to)));
  };
  const emptyRanks = new Map<string, number>();
  const explicitEndpointCardByValue = new Map<string, string>();
  graph.nodes.forEach((node) => {
    const kind = nodeKind(node);
    if (kind !== 'source' && kind !== 'destination') {
      return;
    }
    const value = endpointIdentityValue(node);
    if (value) {
      explicitEndpointCardByValue.set(value, endpointAttachmentInterfaceIdByEndpointId.get(node.id) ?? node.id);
    }
  });
  const endpointIdsWithPathEdges = new Set<string>();
  graph.edges.forEach((edge) => {
    const relation = edgeRelation(edge);
    const pathLike = isGapEdge(edge) || (pathIdsForEdge(edge).length > 0 && !isSupportRelation(relation));
    if (!pathLike) {
      return;
    }
    const from = nodesById.get(edge.fromId);
    const to = nodesById.get(edge.toId);
    if (from && nodeKind(from) === 'endpoint') {
      endpointIdsWithPathEdges.add(from.id);
    }
    if (to && nodeKind(to) === 'endpoint') {
      endpointIdsWithPathEdges.add(to.id);
    }
  });

  const deviceChildIds = new Map<string, Set<string>>();
  const nodeToCardId: Record<string, string> = {};
  const repeatedDeviceStages = repeatedDeviceStagesFromGraphMeta(
    graphMeta ?? {},
    selectedPathId,
    nodesById,
    directDeviceByNodeId,
    vrfByInterfaceId
  );
  const deviceStageByCardId = new Map<string, { deviceId: string; vrfId?: string }>();
  const cloudStageByCardId = new Map<string, RepeatedCloudStage>();
  repeatedCloudStages.stagesByResourceId.forEach((stages) => {
    stages.forEach((stage) => {
      cloudStageByCardId.set(stage.key, stage);
      deviceChildIds.set(stage.key, new Set(stage.interfaceIds));
    });
  });
  const stageCardId = (deviceId: string, stageKey?: string): string =>
    `path-stage:${deviceId}:${stageKey ?? 'unscoped'}`;
  const ensureDeviceStageCard = (deviceId: string, stageKey?: string, vrfId?: string): string => {
    const cardId = stageCardId(deviceId, stageKey);
    deviceStageByCardId.set(cardId, { deviceId, vrfId });
    return cardId;
  };
  const stageCardIdsByInterfaceId = new Map<string, string[]>();
  repeatedDeviceStages.forEach((stages, deviceId) => {
    stages.forEach((stage) => {
      const cardId = ensureDeviceStageCard(deviceId, stage.key, stage.vrfId);
      if (!deviceChildIds.has(cardId)) {
        deviceChildIds.set(cardId, new Set());
      }
      deviceChildIds.get(cardId)?.add(stage.vrfId);
      stage.interfaceIds.forEach((interfaceId) => {
        deviceChildIds.get(cardId)?.add(interfaceId);
        stageCardIdsByInterfaceId.set(
          interfaceId,
          unique([...(stageCardIdsByInterfaceId.get(interfaceId) ?? []), cardId])
        );
      });
    });
  });
  const nodesRequiringUnscopedSplitCard = new Set<string>();
  graph.edges.forEach((edge) => {
    if (isStructuralContainmentEdge(edge, nodesById)) {
      return;
    }
    const edgePathIds = pathIdsForEdge(edge);
    if (selectedPathId !== 'all' && edgePathIds.length > 0 && !edgePathIds.includes(selectedPathId)) {
      return;
    }
    [edge.fromId, edge.toId].forEach((nodeId) => {
      const node = nodesById.get(nodeId);
      if (!node) {
        return;
      }
      const deviceId = nodeKind(node) === 'device' ? node.id : directDeviceByNodeId.get(node.id);
      const stages = deviceId ? repeatedDeviceStages.get(deviceId) : undefined;
      if (!deviceId || !stages) {
        return;
      }
      const vrfId = nodeKind(node) === 'vrf' ? node.id : nodeKind(node) === 'interface' ? vrfByInterfaceId.get(node.id) : undefined;
      if (!vrfId || !stages.some((stage) => stage.vrfId === vrfId)) {
        nodesRequiringUnscopedSplitCard.add(node.id);
      }
    });
  });
  const deviceCardIdForNode = (node: TopologyNode, deviceId: string): string => {
    const stages = repeatedDeviceStages.get(deviceId);
    if (!stages) {
      return deviceId;
    }
    const kind = nodeKind(node);
    if (kind === 'interface') {
      const stageCardIds = stageCardIdsByInterfaceId.get(node.id) ?? [];
      if (stageCardIds.length === 1) {
        return stageCardIds[0];
      }
    }
    if (kind === 'vrf') {
      const matchingStages = stages.filter((stage) => stage.vrfId === node.id);
      if (matchingStages.length === 1) {
        return stageCardId(deviceId, matchingStages[0].key);
      }
    }
    return nodesRequiringUnscopedSplitCard.has(node.id) ? ensureDeviceStageCard(deviceId) : '';
  };

  graph.nodes.forEach((node) => {
    const kind = nodeKind(node);
    const cloudKind = cloudResourceKind(node);
    const role = nodeRole(node);
    if (endpointOwnerPresentation.hiddenTailNodeIds.has(node.id)) {
      nodeToCardId[node.id] = '';
      return;
    }
    const tunnelInterfaceAliasId = tunnelInterfaceAliasByNodeId.get(node.id);
    if (tunnelInterfaceAliasId) {
      const tunnelInterface = nodesById.get(tunnelInterfaceAliasId);
      const tunnelDeviceId = tunnelInterface ? directDeviceByNodeId.get(tunnelInterface.id) : undefined;
      nodeToCardId[node.id] = tunnelInterface && tunnelDeviceId ? deviceCardIdForNode(tunnelInterface, tunnelDeviceId) : '';
      return;
    }
    const ownerInterfaceId = endpointOwnerPresentation.interfaceIdByEndpointNodeId.get(node.id);
    if (ownerInterfaceId && kind !== 'interface') {
      const ownerInterface = nodesById.get(ownerInterfaceId);
      const ownerDeviceId = ownerInterface ? directDeviceByNodeId.get(ownerInterface.id) : undefined;
      nodeToCardId[node.id] = ownerInterface && ownerDeviceId ? deviceCardIdForNode(ownerInterface, ownerDeviceId) : '';
      return;
    }
    const attachedInterfaceId = endpointAttachmentInterfaceIdByEndpointId.get(node.id);
    if (attachedInterfaceId && kind !== 'interface') {
      nodeToCardId[node.id] = attachedInterfaceId;
      return;
    }
    if (kind === 'endpoint' && role !== 'source' && role !== 'destination' && explicitEndpointCardByValue.size > 0) {
      const aliasCardId = explicitEndpointCardByValue.get(endpointIdentityValue(node));
      if (aliasCardId) {
        nodeToCardId[node.id] = aliasCardId;
        return;
      }
      if (!endpointIdsWithPathEdges.has(node.id)) {
        nodeToCardId[node.id] = '';
        return;
      }
    }
    const foldedSubnetId = kind === 'vlan' && role !== 'source' && role !== 'destination' ? subnetByVlanId.get(node.id) : undefined;
    if (foldedSubnetId) {
      nodeToCardId[node.id] = foldedSubnetId;
      return;
    }
    if (cloudKind === 'vpc' || cloudKind === 'tgw_attachment') {
      // VPC and TGW attachment are connectivity context. They remain in the
      // backend graph but do not become forwarding cards.
      nodeToCardId[node.id] = '';
      return;
    }
    if (cloudKind === 'vpn_connection') {
      nodeToCardId[node.id] = tgwByVpnConnectionId.get(node.id) ?? '';
      return;
    }
    const cloudStageOwnerId = cloudKind === 'tgw' || cloudKind === 'subnet' ? node.id : directDeviceByNodeId.get(node.id);
    const cloudStageCardId = cloudStageOwnerId ? repeatedCloudStages.stagesByResourceId.get(cloudStageOwnerId)?.[0]?.key : undefined;
    if (cloudStageCardId) {
      nodeToCardId[node.id] = cloudStageCardId;
      return;
    }
    const deviceId = directDeviceByNodeId.get(node.id);
    const hideInDevice = Boolean(deviceId) && kind !== 'source' && kind !== 'destination' && role !== 'source' && role !== 'destination';
    if (hideInDevice && deviceId) {
      const cardId = deviceCardIdForNode(node, deviceId);
      if (cardId) {
        if (!deviceChildIds.has(cardId)) {
          deviceChildIds.set(cardId, new Set());
        }
        deviceChildIds.get(cardId)?.add(node.id);
      }
      nodeToCardId[node.id] = cardId;
    } else if (kind === 'device' && repeatedDeviceStages.has(node.id)) {
      nodeToCardId[node.id] = nodesRequiringUnscopedSplitCard.has(node.id) ? ensureDeviceStageCard(node.id) : '';
    } else {
      nodeToCardId[node.id] = node.id;
    }
  });

  const cardRoots: PathCardRoot[] = [
    ...graph.nodes
      .filter((node) => nodeToCardId[node.id] === node.id)
      .map((node) => ({ id: node.id, node })),
    ...Array.from(deviceStageByCardId.entries())
      .map(([id, stage]) => {
        const node = nodesById.get(stage.deviceId);
        return node ? { id, node } : undefined;
      })
      .filter((root): root is PathCardRoot => Boolean(root)),
    ...Array.from(cloudStageByCardId.entries())
      .map(([id, stage]) => {
        const node = nodesById.get(stage.resourceId);
        return node ? { id, node } : undefined;
      })
      .filter((root): root is PathCardRoot => Boolean(root))
  ];

  const cardDrafts: Array<Omit<PathCard, 'x' | 'y' | 'rank'>> = cardRoots.map((root) => {
    const node = root.node;
    const cloudStage = cloudStageByCardId.get(root.id);
    const children = Array.from(deviceChildIds.get(root.id) ?? []);
    const childNodes = children.map((childId) => nodesById.get(childId)).filter((child): child is TopologyNode => Boolean(child));
    const vrfs = childNodes.filter((child) => nodeKind(child) === 'vrf');
    const cloudVpnConnectionIds =
      cloudResourceKind(node) === 'tgw'
        ? Array.from(tgwByVpnConnectionId.entries())
            .filter(([, tgwId]) => tgwId === node.id)
            .map(([vpnConnectionId]) => vpnConnectionId)
        : [];
    const cloudVpnGroups = cloudVpnConnectionIds
      .map((vpnConnectionId) => {
        const vpnConnection = nodesById.get(vpnConnectionId);
        const tunnels = childNodes.filter((child) => vpnConnectionByTunnelId.get(child.id) === vpnConnectionId);
        if (!vpnConnection || tunnels.length === 0) return undefined;
        const groupLayout = layoutDeviceInterfacesForPath(
          tunnels,
          interfaceRolesById,
          nodeToCardId,
          emptyRanks,
          subnetIdsByInterfaceId,
          interfaceFlowRolesByPath,
          selectedPathId
        );
        return {
          node: vpnConnection,
          pathIds: pathIdsForNode(vpnConnection),
          interfaces: groupLayout.interfaces,
          interfaceLayoutById: groupLayout.interfaceLayoutById
        };
      })
      .filter((group): group is PathCardVrfGroup => Boolean(group));
    const cloudTunnelIds = new Set(cloudVpnGroups.flatMap((group) => group.interfaces.map((tunnel) => tunnel.id)));
    const allInterfaces = childNodes
      .filter((child) => nodeKind(child) === 'interface' && !cloudTunnelIds.has(child.id))
      .sort((left, right) => compareInterfacesForDisplay(left, right, interfaceRolesById));
    const vlanChips =
      nodeKind(node) === 'subnet'
        ? Array.from(vlanIdsBySubnetId.get(node.id) ?? [])
            .map((vlanId) => nodesById.get(vlanId))
            .filter((vlan): vlan is TopologyNode => Boolean(vlan))
            .filter((vlan) => selectedPathId === 'all' || pathIdsForNode(vlan).includes(selectedPathId))
        : [];
    const vrfGroups = vrfs.map((vrf) => {
      const groupLayout = layoutDeviceInterfacesForPath(
        allInterfaces.filter((intf) => vrfByInterfaceId.get(intf.id) === vrf.id),
        interfaceRolesById,
        nodeToCardId,
        emptyRanks,
        subnetIdsByInterfaceId,
        interfaceFlowRolesByPath,
        selectedPathId
      );
      return {
        node: vrf,
        pathIds: pathIdsForNode(vrf),
        interfaces: groupLayout.interfaces,
        interfaceLayoutById: groupLayout.interfaceLayoutById
      };
    });
    const groupedInterfaceIds = new Set(vrfGroups.flatMap((group) => group.interfaces.map((intf) => intf.id)));
    const ungroupedInterfaces = allInterfaces.filter((intf) => !groupedInterfaceIds.has(intf.id));
    const ungroupedLayout = layoutDeviceInterfacesForPath(
      ungroupedInterfaces,
      interfaceRolesById,
      nodeToCardId,
      emptyRanks,
      subnetIdsByInterfaceId,
      interfaceFlowRolesByPath,
      selectedPathId,
      cloudStage?.interfaceFlowRoles
    );
    const kind = nodeKind(node);
    const interfaceRoles = interfaceRolesById.get(node.id) ?? [];
    const standaloneInterfaceRole =
      kind === 'interface' && !directDeviceByNodeId.has(node.id)
        ? interfaceRoles.includes('source')
          ? 'source'
          : interfaceRoles.includes('destination')
            ? 'destination'
            : undefined
        : undefined;
    const cardKind = (kind === 'interface' ? standaloneInterfaceRole ?? 'other' : kind) as CardKind;
    const memberNodeIds = unique([node.id, ...children, ...vlanChips.map((vlan) => vlan.id)]);
    const cardPathIds = unique(memberNodeIds.flatMap((nodeId) => pathIdsForNode(nodesById.get(nodeId) as TopologyNode)));
    const size = cardSize(cardKind, vrfGroups, cloudVpnGroups, ungroupedLayout.interfaces, ungroupedLayout.interfaceLayoutById);
    return {
      id: root.id,
      nodeId: node.id,
      node,
      kind: cardKind,
      level: nodeLevel(node),
      role: nodeTraceRole(node) ?? standaloneInterfaceRole,
      pathIds: cardPathIds,
      memberNodeIds,
      width: size.width,
      height: size.height,
      vrfGroups,
      vpnGroups: cloudVpnGroups,
      interfaces: ungroupedLayout.interfaces,
      interfaceLayoutById: ungroupedLayout.interfaceLayoutById,
      vlanChips,
      cloudResource: cloudResourceKind(node),
      subnetVpc: vpcBySubnetId.get(node.id) ? nodesById.get(vpcBySubnetId.get(node.id)!) : undefined
    };
  });

  const cardKindById = new Map(cardDrafts.map((card) => [card.id, card.kind]));
  const cardRoleById = new Map(cardDrafts.map((card) => [card.id, cardTraceRole(card)]));
  const flowCardEdgeCandidates: PathFlowCardEdge[] = flowSourceEdges
    .filter((edge) => !isStructuralContainmentEdge(edge, nodesById))
    .map((edge) => {
      const edgePathIds = pathIdsForEdge(edge);
      const presentation = asRecord(getProperties(edge).presentation);
      const fromCardId =
        (isPresentationSequenceEdge(edge) ? stringValue(presentation?.fromCardId) : undefined) ?? nodeToCardId[edge.fromId];
      const toCardId =
        (isPresentationSequenceEdge(edge) ? stringValue(presentation?.toCardId) : undefined) ?? nodeToCardId[edge.toId];
      const candidate: PathFlowCardEdge = {
        edge,
        fromCardId,
        toCardId,
        fromNodeId: edge.fromId,
        toNodeId: edge.toId,
        pathIds: edgePathIds,
        swapped: false
      };
      const fromKind = cardKindById.get(fromCardId);
      const toKind = cardKindById.get(toCardId);
      const fromRole = cardRoleById.get(fromCardId);
      const toRole = cardRoleById.get(toCardId);
      const endpointSubnetSupport =
        edgePathIds.length > 0 &&
        isSupportRelation(edgeRelation(edge)) &&
        ((((fromKind === 'source' || fromKind === 'destination') || fromRole === 'source' || fromRole === 'destination') &&
          (toKind === 'subnet' || toKind === 'vlan')) ||
          ((((toKind === 'source' || toKind === 'destination') || toRole === 'source' || toRole === 'destination')) &&
            (fromKind === 'subnet' || fromKind === 'vlan')));
      const hopInterfaceSupport =
        edge.directed !== true &&
        edgePathIds.length > 0 &&
        isSupportRelation(edgeRelation(edge)) &&
        interfaceFlowRoleForCandidate(candidate, interfaceFlowRolesByPath, selectedPathId) !== undefined;
      return {
        ...candidate,
        flow:
          edgeAppliesToSelectedPath(edgePathIds, selectedPathId) &&
          (isPresentationSequenceEdge(edge) || !isCloudContextEdge(edge) || !edgePathIds.some((pathId) => sequencePathIds.has(pathId))) &&
          (isPathFlowEdge(edge, edgePathIds) || endpointSubnetSupport || hopInterfaceSupport)
      };
    })
    .filter((edge) => edge.fromCardId && edge.toCardId && edge.fromCardId !== edge.toCardId && edge.flow)
    .map((edge) => ({
      edge: edge.edge,
      fromCardId: edge.fromCardId,
      toCardId: edge.toCardId,
      fromNodeId: edge.fromNodeId,
      toNodeId: edge.toNodeId,
      pathIds: edge.pathIds,
      swapped: edge.swapped
    }));
  const sourceSubnetCardIds = new Set<string>();
  const destinationSubnetCardIds = new Set<string>();
  flowCardEdgeCandidates.forEach((candidate) => {
    const fromKind = cardKindById.get(candidate.fromCardId);
    const toKind = cardKindById.get(candidate.toCardId);
    const fromRole = cardRoleById.get(candidate.fromCardId);
    const toRole = cardRoleById.get(candidate.toCardId);
    if ((fromKind === 'source' || fromRole === 'source') && (toKind === 'subnet' || toKind === 'vlan')) {
      sourceSubnetCardIds.add(candidate.toCardId);
    } else if ((toKind === 'source' || toRole === 'source') && (fromKind === 'subnet' || fromKind === 'vlan')) {
      sourceSubnetCardIds.add(candidate.fromCardId);
    }
    if ((fromKind === 'destination' || fromRole === 'destination') && (toKind === 'subnet' || toKind === 'vlan')) {
      destinationSubnetCardIds.add(candidate.toCardId);
    } else if ((toKind === 'destination' || toRole === 'destination') && (fromKind === 'subnet' || fromKind === 'vlan')) {
      destinationSubnetCardIds.add(candidate.fromCardId);
    }
  });
  const pathFlowCardEdges = flowCardEdgeCandidates.filter((candidate) => {
    const fromKind = cardKindById.get(candidate.fromCardId);
    const toKind = cardKindById.get(candidate.toCardId);
    const fromRole = cardRoleById.get(candidate.fromCardId);
    const toRole = cardRoleById.get(candidate.toCardId);
    const sourceToDevice =
      ((fromKind === 'source' || fromRole === 'source') && toKind === 'device') ||
      (fromKind === 'device' && (toKind === 'source' || toRole === 'source'));
    if (sourceToDevice && sourceSubnetCardIds.size > 0 && isSupportRelation(edgeRelation(candidate.edge))) {
      return false;
    }
    const deviceToDestination =
      (fromKind === 'device' && (toKind === 'destination' || toRole === 'destination')) ||
      ((fromKind === 'destination' || fromRole === 'destination') && toKind === 'device');
    if (deviceToDestination && destinationSubnetCardIds.size > 0 && isSupportRelation(edgeRelation(candidate.edge))) {
      return false;
    }
    return true;
  });
  const orientedFlowCardEdges = pathFlowCardEdges.map((candidate) =>
    orientPathFlowCardEdge(
      candidate,
      cardKindById,
      cardRoleById,
      sourceSubnetCardIds,
      destinationSubnetCardIds,
      interfaceFlowRolesByPath,
      selectedPathId
    )
  );
  const candidatePathFlowEdgeIds = new Set(orientedFlowCardEdges.map((edge) => edge.edge.id));
  const { edges: dedupedFlowCardEdges, keptEdgeIds: dedupedPathFlowEdgeIds } = dedupePathFlowCardEdges(
    orientedFlowCardEdges,
    nodesById,
    nodeToCardId
  );
  const pathFlowEdgeById = new Map(dedupedFlowCardEdges.map((edge) => [edge.edge.id, edge]));
  const edgeCardPairs = dedupedFlowCardEdges.map((edge) => ({ fromCardId: edge.fromCardId, toCardId: edge.toCardId }));
  const ranks = rankCards(cardDrafts, edgeCardPairs);
  const orderedCardDrafts = cardDrafts.map((card) => {
    if (card.kind !== 'device') {
      return card;
    }
    const orderedVrfGroups = card.vrfGroups.map((group) => {
      const groupLayout = layoutDeviceInterfacesForPath(
        group.interfaces,
        interfaceRolesById,
        nodeToCardId,
        ranks,
        subnetIdsByInterfaceId,
        interfaceFlowRolesByPath,
        selectedPathId
      );
      return {
        ...group,
        interfaces: groupLayout.interfaces,
        interfaceLayoutById: groupLayout.interfaceLayoutById
      };
    });
    const orderedUngroupedLayout = layoutDeviceInterfacesForPath(
      card.interfaces,
      interfaceRolesById,
      nodeToCardId,
      ranks,
      subnetIdsByInterfaceId,
      interfaceFlowRolesByPath,
      selectedPathId,
      cloudStageByCardId.get(card.id)?.interfaceFlowRoles
    );
    const size = cardSize(card.kind, orderedVrfGroups, card.vpnGroups, orderedUngroupedLayout.interfaces, orderedUngroupedLayout.interfaceLayoutById);
    return {
      ...card,
      width: size.width,
      height: size.height,
      vrfGroups: orderedVrfGroups,
      interfaces: orderedUngroupedLayout.interfaces,
      interfaceLayoutById: orderedUngroupedLayout.interfaceLayoutById
    };
  });
  const cardsByRank = new Map<number, Array<Omit<PathCard, 'x' | 'y' | 'rank'>>>();
  orderedCardDrafts.forEach((card) => {
    const rank = ranks.get(card.id) ?? 0;
    if (!cardsByRank.has(rank)) {
      cardsByRank.set(rank, []);
    }
    cardsByRank.get(rank)?.push(card);
  });

  const laidOutCards: PathCard[] = [];
  Array.from(cardsByRank.entries())
    .sort(([left], [right]) => left - right)
    .forEach(([rank, cards]) => {
      let yCursor = CANVAS_PAD_Y;
      cards
        .sort((left, right) => {
          const byLane = cardLane(left) - cardLane(right);
          return byLane !== 0 ? byLane : displayName(left.node).localeCompare(displayName(right.node));
        })
        .forEach((card) => {
          const x = CANVAS_PAD_X + rank * RANK_GAP;
          const y = yCursor;
          laidOutCards.push({ ...card, x, y, rank });
          yCursor += card.height + ROW_GAP;
        });
    });

  const cardsById = new Map(laidOutCards.map((card) => [card.id, card]));
  const routeRects = laidOutCards.map((card) => cardRouteRect(card));
  const visualEdges: PathEdgeView[] = [];
  const seenVisualEdges = new Set<string>();
  flowSourceEdges.forEach((edge) => {
    if (isStructuralContainmentEdge(edge, nodesById) || isPathPresentationContextEdge(edge)) {
      return;
    }
    // Ordered synthetic hop edges can intentionally point at a later visual
    // stage of a repeated cloud resource.  Consult that presentation mapping
    // before the generic node-to-card lookup, otherwise both ends resolve to
    // the resource's first stage and the edge is incorrectly treated as
    // internal to one card.
    const presentation = asRecord(getProperties(edge).presentation);
    const sourceFromCardId =
      (isPresentationSequenceEdge(edge) ? stringValue(presentation?.fromCardId) : undefined) ?? nodeToCardId[edge.fromId];
    const sourceToCardId =
      (isPresentationSequenceEdge(edge) ? stringValue(presentation?.toCardId) : undefined) ?? nodeToCardId[edge.toId];
    const sourceFromCard = sourceFromCardId ? cardsById.get(sourceFromCardId) : undefined;
    const sourceToCard = sourceToCardId ? cardsById.get(sourceToCardId) : undefined;
    if (!sourceFromCard || !sourceToCard || sourceFromCard.id === sourceToCard.id) {
      return;
    }
    const edgePathIds = pathIdsForEdge(edge);
    if (
      !isPresentationSequenceEdge(edge) &&
      isCloudContextEdge(edge) &&
      !isGapEdge(edge) &&
      edgePathIds.some((pathId) => sequencePathIds.has(pathId))
    ) {
      return;
    }
    if (selectedPathId !== 'all' && edgePathIds.length > 0 && !edgePathIds.includes(selectedPathId)) {
      return;
    }
    const oriented = pathFlowEdgeById.get(edge.id);
    if (candidatePathFlowEdgeIds.has(edge.id) && !oriented) {
      return;
    }
    const kind: PathEdgeView['kind'] = isGapEdge(edge) ? 'gap' : oriented ? 'path' : 'support';
    const fromCard = oriented ? cardsById.get(oriented.fromCardId) : sourceFromCard;
    const toCard = oriented ? cardsById.get(oriented.toCardId) : sourceToCard;
    const fromNodeId = oriented?.fromNodeId ?? edge.fromId;
    const toNodeId = oriented?.toNodeId ?? edge.toId;
    if (!fromCard || !toCard || fromCard.id === toCard.id) {
      return;
    }
    if (selectedPathId !== 'all' && edgePathIds.length === 0 && kind === 'support') {
      return;
    }
    if (kind === 'path' && !dedupedPathFlowEdgeIds.has(edge.id)) {
      return;
    }
    // Card ranks are layout hints. A valid backend path edge may span ranks
    // when several paths share cards, so rank distance must not hide evidence.
    if (kind === 'support' && cardTraceRole(fromCard) === 'source') {
      return;
    }
    if (kind === 'support' && cardTraceRole(toCard) === 'destination') {
      return;
    }
    const visualId =
      kind === 'gap'
        ? `${edge.id}:${fromNodeId}:${toNodeId}:${fromCard.id}:${toCard.id}`
        : kind === 'support'
          ? `${fromNodeId}:${toNodeId}:${fromCard.id}:${toCard.id}:${kind}:${edgePathIds.join(',')}`
          : `${fromNodeId}:${toNodeId}:${fromCard.id}:${toCard.id}:${kind}:${edgePathIds.join(',')}`;
    if (seenVisualEdges.has(visualId)) {
      return;
    }
    seenVisualEdges.add(visualId);
    const from = bestEdgeEndpointForNode(
      tunnelInterfaceAliasByNodeId.get(fromNodeId) ?? fromNodeId,
      fromCard,
      toCard,
      edgePathIds,
      subnetIdsByInterfaceId,
      vlanIdsByInterfaceId
    );
    const to = bestEdgeEndpointForNode(
      tunnelInterfaceAliasByNodeId.get(toNodeId) ?? toNodeId,
      toCard,
      fromCard,
      edgePathIds,
      subnetIdsByInterfaceId,
      vlanIdsByInterfaceId
    );
    const routeObstacles = routeRects.filter((rect) => {
      if (fromNodeId !== fromCard.id && rect.id === fromCard.id) {
        return false;
      }
      if (toNodeId !== toCard.id && rect.id === toCard.id) {
        return false;
      }
      return true;
    });
    const route = buildRectAvoidingPathRoute(from, to, routeObstacles);
    visualEdges.push({
      id: edge.id,
      edge,
      fromCardId: fromCard.id,
      toCardId: toCard.id,
      label: pathRelatedEdgeLabel(edge, kind),
      hasExplicitLabel: hasExplicitEdgeLabel(edge),
      pathIds: edgePathIds,
      kind,
      color: edgeColor(kind),
      from,
      to,
      route
    });
  });

  const width = Math.max(960, ...laidOutCards.map((card) => card.x + card.width + CANVAS_PAD_X));
  const height = Math.max(560, ...laidOutCards.map((card) => card.y + card.height + CANVAS_PAD_Y));
  const evidenceGaps = uniqueEvidenceGaps([
    ...evidenceGapViewsFromPathPayload(graphMeta, 'graph'),
    ...evidenceGapViewsFromPathPayload(graphMeta?.path, 'graph:path'),
    ...evidenceGapViewsFromPathPayload(graphMeta?.pathResult, 'graph:pathResult'),
    ...evidenceGapViewsFromPathPayload(graphMeta?.pathPayload, 'graph:pathPayload'),
    ...graph.nodes.flatMap((node) =>
      evidenceGapViewsFromPathPayload(getProperties(node), `node:${node.id}`).map((gap) => ({
        ...gap,
        pathIds: unique([...gap.pathIds, ...pathIdsForNode(node)])
      }))
    ),
    ...graph.edges.flatMap((edge) =>
      evidenceGapViewsFromPathPayload(getProperties(edge), `edge:${edge.id}`).map((gap) => ({
        ...gap,
        pathIds: unique([...gap.pathIds, ...pathIdsForEdge(edge)])
      }))
    )
  ]);

  return { pathIds, cards: laidOutCards, edges: visualEdges, evidenceGaps, nodeToCardId, width, height };
};

const cardMatchesPath = (card: PathCard, selectedPathId: string | 'all'): boolean =>
  selectedPathId === 'all' || card.pathIds.includes(selectedPathId);

const cardVisibleForPath = (card: PathCard, selectedPathId: string | 'all'): boolean =>
  selectedPathId === 'all' || card.pathIds.includes(selectedPathId) || (card.pathIds.length === 0 && cardTraceRole(card) !== undefined);

const cardSharedWithOtherPaths = (card: PathCard, selectedPathId: string | 'all'): boolean =>
  selectedPathId !== 'all' && card.pathIds.includes(selectedPathId) && card.pathIds.length > 1;

const edgeSharedWithOtherPaths = (edge: PathEdgeView, selectedPathId: string | 'all'): boolean =>
  edge.pathIds.length > 1 && (selectedPathId === 'all' || edge.pathIds.includes(selectedPathId));

const edgeUntaggedGapContext = (edge: PathEdgeView, selectedPathId: string | 'all'): boolean =>
  selectedPathId !== 'all' && edge.kind === 'gap' && edge.pathIds.length === 0;

const edgeVisibleForPath = (edge: PathEdgeView, selectedPathId: string | 'all'): boolean =>
  selectedPathId === 'all' || edge.pathIds.includes(selectedPathId) || edgeUntaggedGapContext(edge, selectedPathId);

const gapMatchesPath = (gap: EvidenceGapView, selectedPathId: string | 'all'): boolean =>
  selectedPathId === 'all' || gap.pathIds.includes(selectedPathId);

export const evidenceGapsForSelectedPath = (
  gaps: EvidenceGapView[],
  selectedPathId: string | 'all'
): EvidenceGapView[] => gaps.filter((gap) => gapMatchesPath(gap, selectedPathId));

const uniqueEvidenceGaps = (gaps: EvidenceGapView[]): EvidenceGapView[] => {
  const seen = new Set<string>();
  const out: EvidenceGapView[] = [];
  gaps.forEach((gap) => {
    const key = `${gap.code}:${gap.summary}:${gap.pathIds.join(',')}:${gap.target ?? ''}`;
    if (seen.has(key)) {
      return;
    }
    seen.add(key);
    out.push(gap);
  });
  return out;
};

const interfaceTooltip = (node: TopologyNode): string => interfaceTitle(node);

const shortPathLabel = (pathId: string): string => {
  const trimmed = pathId.trim();
  const suffix = trimmed.match(/(?:^|[-_:])p(?:ath)?[-_:]?(\d+)$/i)?.[1] ?? trimmed.match(/(?:primary|secondary)$/i)?.[0];
  if (suffix) {
    return suffix.toLowerCase() === 'primary' ? 'P1' : suffix.toLowerCase() === 'secondary' ? 'P2' : `P${suffix}`;
  }
  const last = trimmed.split(/[-_:]/).filter(Boolean).pop();
  return last ? last.toUpperCase().slice(0, 12) : trimmed.slice(0, 12);
};

const pathMembershipLabel = (pathIds: string[], selectedPathId: string | 'all'): string => {
  if (pathIds.length === 0) {
    return 'Context';
  }
  if (pathIds.length > 1) {
    return 'Shared';
  }
  if (selectedPathId !== 'all') {
    return pathIds.includes(selectedPathId) ? shortPathLabel(selectedPathId) : pathIds.map(shortPathLabel).join(' / ');
  }
  const labels = pathIds.map(shortPathLabel).join(' / ');
  return labels;
};

const cardTitle = (card: PathCard): string => {
  const title = displayName(card.node);
  if (card.kind === 'source' || card.kind === 'destination') {
    return endpointIdentityValue(card.node) || title;
  }
  return title;
};

const cloudSubnetCidr = (node: TopologyNode): string | undefined => {
  const aws = asRecord(getProperties(node).aws);
  return stringValue(aws?.ipv4CidrBlock) ?? cloudPresentationValue(node, 'cidr');
};

const cardDetail = (card: PathCard): string | undefined => {
  if (card.cloudResource === 'subnet') {
    const vpc = card.subnetVpc ? displayName(card.subnetVpc) : undefined;
    const cidr = cloudSubnetCidr(card.node);
    return [vpc ? `VPC ${vpc}` : undefined, cidr].filter(Boolean).join(' · ') || undefined;
  }
  if (card.cloudResource === 'eni') {
    return interfaceAddressValues(card.node).join(', ') || undefined;
  }
  return undefined;
};

const cardKicker = (card: PathCard): string => {
  switch (card.cloudResource) {
    case 'tgw':
      return 'Transit Gateway';
    case 'ec2':
      return 'EC2 instance';
    case 'eni':
      return 'ENI';
    case 'terminal':
      return 'Stopped resource';
  }
  switch (card.kind) {
    case 'source':
      return 'Source';
    case 'destination':
      return 'Destination';
    case 'subnet':
      return 'Subnet';
    case 'vlan':
      return 'VLAN';
    default:
      return card.level || card.kind;
  }
};

const edgePath = (edge: PathEdgeView): string =>
  edge.route.map((point, index) => `${index === 0 ? 'M' : 'L'} ${point.x} ${point.y}`).join(' ');

const edgeLabelPoint = (edge: PathEdgeView): Point => {
  if (edge.route.length < 2) {
    return { x: edge.from.x, y: edge.from.y };
  }
  const lengths = edge.route.slice(1).map((point, index) => Math.hypot(point.x - edge.route[index].x, point.y - edge.route[index].y));
  const total = lengths.reduce((sum, length) => sum + length, 0);
  let remaining = total / 2;
  for (let index = 0; index < lengths.length; index += 1) {
    const length = lengths[index];
    if (remaining <= length || index === lengths.length - 1) {
      const from = edge.route[index];
      const to = edge.route[index + 1];
      const ratio = length <= ROUTE_EPSILON ? 0 : remaining / length;
      return { x: from.x + (to.x - from.x) * ratio, y: from.y + (to.y - from.y) * ratio };
    }
    remaining -= length;
  }
  return { x: edge.to.x, y: edge.to.y };
};

const interfacePortRole = (roles: EndpointRole[]): PathPortRole => {
  const hasSource = roles.includes('source');
  const hasDestination = roles.includes('destination');
  if (hasSource && hasDestination) {
    return 'source-destination';
  }
  if (hasSource) {
    return 'source';
  }
  if (hasDestination) {
    return 'destination';
  }
  return 'default';
};

const clampZoom = (value: number): number => Math.min(MAX_ZOOM, Math.max(MIN_ZOOM, Number(value.toFixed(2))));

export const PathRelatedViewRenderer = ({
  topology,
  graphKey,
  selectedNodeIds,
  selectedEdgeIds,
  onNodeSelect,
  onEdgeSelect,
  onPaneClick
}: PathRelatedViewRendererProps) => {
  const [selectedPathId, setSelectedPathId] = useState<string | 'all'>('all');
  const [activeTab, setActiveTab] = useState<'graph' | 'evidence'>('graph');
  const [zoom, setZoom] = useState(1);
  const topologyPathIds = useMemo(() => (topology ? graphPathIds(topology) : []), [topology]);
  const effectiveSelectedPathId =
    selectedPathId !== 'all' && !topologyPathIds.includes(selectedPathId) ? 'all' : selectedPathId;
  const renderSelectedPathId = effectiveSelectedPathId === 'all' && topologyPathIds.length === 1 ? topologyPathIds[0] : effectiveSelectedPathId;
  const model = useMemo(
    () => (topology ? buildPathRelatedViewModel(topology, renderSelectedPathId) : undefined),
    [renderSelectedPathId, topology]
  );
  const markerKey = graphKey || topology?.graphId || 'path-related-view';
  const markerBaseId = `topology-path-arrow-${markerKey.replace(/[^a-zA-Z0-9_-]/g, '-')}`;
  const markerIdForKind = (kind: PathEdgeView['kind']): string => `${markerBaseId}-${kind}`;
  const interfaceRolesById = useMemo(
    () => (topology ? interfaceEndpointRoleMap(topology, renderSelectedPathId) : new Map<string, EndpointRole[]>()),
    [renderSelectedPathId, topology]
  );
  const interfaceFlowRolesByPath = useMemo(
    () => (topology ? interfaceFlowRolesFromGraphMeta(asRecord(topology.meta) ?? {}) : new Map()),
    [topology]
  );

  useEffect(() => {
    if (effectiveSelectedPathId !== selectedPathId) {
      setSelectedPathId(effectiveSelectedPathId);
    }
  }, [effectiveSelectedPathId, selectedPathId]);

  if (!topology || !model) {
    return <div className="topology-path-view topology-path-view--empty">No path detail graph available.</div>;
  }

  const handlePaneClick = (event: ReactMouseEvent<HTMLDivElement>) => {
    onPaneClick?.(event);
  };
  const handleWheel = (event: ReactWheelEvent<HTMLDivElement>) => {
    if (!event.metaKey && !event.ctrlKey) {
      return;
    }
    event.preventDefault();
    event.stopPropagation();
    setZoom((current) => clampZoom(current + (event.deltaY > 0 ? -ZOOM_STEP : ZOOM_STEP)));
  };
  const evidenceScopePathId = renderSelectedPathId;
  const selectedEvidenceGaps = evidenceGapsForSelectedPath(model.evidenceGaps, evidenceScopePathId);
  const visibleCards = model.cards.filter((card) => cardVisibleForPath(card, renderSelectedPathId));
  const visibleCardIds = new Set(visibleCards.map((card) => card.id));
  const visibleEdges = model.edges.filter(
    (edge) =>
      visibleCardIds.has(edge.fromCardId) &&
      visibleCardIds.has(edge.toCardId) &&
      edgeVisibleForPath(edge, renderSelectedPathId)
  );
  const portsById = new Map<string, PathPortView>();
  visibleEdges.forEach((edge) => {
    [edge.from, edge.to].forEach((anchor) => {
      if (!anchor.portNodeId || portsById.has(anchor.portNodeId)) {
        return;
      }
      portsById.set(anchor.portNodeId, {
        key: anchor.portNodeId,
        x: anchor.x,
        y: anchor.y,
        role: interfacePortRole(interfaceRolesById.get(anchor.portNodeId) ?? [])
      });
    });
  });
  const visiblePorts = Array.from(portsById.values());
  const zoomedWidth = model.width * zoom;
  const zoomedHeight = model.height * zoom;
  const dualRolePortGradientId = `${markerBaseId}-port-dual`;
  const renderInterfaceChip = (intf: TopologyNode, slot?: PathInterfaceSlot) => {
    const roles = interfaceRolesById.get(intf.id) ?? [];
    const forwardingRoles = interfaceFlowRolesForSelection(intf.id, interfaceFlowRolesByPath, renderSelectedPathId);
    const tooltip = interfaceTooltip(intf);
    return (
      <span
        key={intf.id}
        className={[
          'topology-path-view__interface-chip',
          roles.includes('source') ? 'is-source' : '',
          roles.includes('destination') ? 'is-destination' : '',
          forwardingRoles.size > 0 ? 'is-forwarding' : ''
        ]
          .filter(Boolean)
          .join(' ')}
        title={tooltip}
        data-tooltip={tooltip}
        style={slot ? { gridColumnStart: slot.column + 1, gridRowStart: slot.row + 1 } : undefined}>
        <span className="topology-path-view__interface-chip-dot" aria-hidden="true" />
        <span className="topology-path-view__interface-chip-text">{interfaceChipLabel(intf)}</span>
      </span>
    );
  };

  return (
    <div className="topology-diagram__canvas topology-path-view" data-testid="topology-path-related-view">
      <div className="topology-path-view__toolbar" onClick={(event) => event.stopPropagation()}>
        <div>
          <span className="topology-path-view__eyebrow">Path related view</span>
          <strong>Directional path detail</strong>
        </div>
        {model.pathIds.length > 1 ? (
          <div className="topology-path-view__path-toggle" aria-label="Path display toggles">
            <button
              type="button"
              className={effectiveSelectedPathId === 'all' ? 'is-active' : ''}
              onClick={() => setSelectedPathId('all')}
            >
              All paths
            </button>
            {model.pathIds.map((pathId) => (
              <button
                key={pathId}
                type="button"
                className={effectiveSelectedPathId === pathId ? 'is-active' : ''}
                onClick={() => setSelectedPathId(pathId)}
                title={pathId}
              >
                {shortPathLabel(pathId)}
              </button>
            ))}
          </div>
        ) : null}
        <div className="topology-path-view__zoom-controls" aria-label="Path canvas zoom controls">
          <button type="button" onClick={() => setZoom((current) => clampZoom(current - ZOOM_STEP))}>
            -
          </button>
          <span>{Math.round(zoom * 100)}%</span>
          <button type="button" onClick={() => setZoom((current) => clampZoom(current + ZOOM_STEP))}>
            +
          </button>
          <button type="button" onClick={() => setZoom(1)}>
            Reset
          </button>
        </div>
        <div className="topology-path-view__tabs" role="tablist" aria-label="Path detail tabs">
          <button
            type="button"
            role="tab"
            aria-selected={activeTab === 'graph'}
            className={activeTab === 'graph' ? 'is-active' : ''}
            onClick={() => setActiveTab('graph')}
          >
            Graph
          </button>
          <button
            type="button"
            role="tab"
            aria-selected={activeTab === 'evidence'}
            className={activeTab === 'evidence' ? 'is-active' : ''}
            onClick={() => setActiveTab('evidence')}
          >
            Evidence gaps
            <span>{selectedEvidenceGaps.length}</span>
          </button>
        </div>
      </div>
      <div className="topology-path-view__scroll" onClick={handlePaneClick} onWheel={handleWheel}>
        {activeTab === 'evidence' ? (
          <div className="topology-path-view__evidence-panel" onClick={(event) => event.stopPropagation()}>
            <div className="topology-path-view__evidence-header">
              <span>Evidence gaps</span>
              <strong>
                {selectedEvidenceGaps.length} for {evidenceScopePathId === 'all' ? 'all visible paths' : shortPathLabel(evidenceScopePathId)}
              </strong>
            </div>
            {selectedEvidenceGaps.length > 0 ? (
              <div className="topology-path-view__evidence-list">
                {selectedEvidenceGaps.map((gap) => (
                  <article key={gap.id} className={`topology-path-view__evidence-item topology-path-view__evidence-item--${gap.severity ?? 'warning'}`}>
                    <div className="topology-path-view__evidence-item-header">
                      <strong>{gap.code}</strong>
                      <span>{gap.severity ?? 'warning'}</span>
                    </div>
                    <p>{gap.summary}</p>
                    <div className="topology-path-view__evidence-meta">
                      {gap.pathIds.length > 0 ? <span>Paths: {gap.pathIds.map(shortPathLabel).join(' / ')}</span> : <span>Applies to selected result</span>}
                      {gap.family ? <span>Family: {gap.family}</span> : null}
                      {gap.target ? <span>Target: {gap.target}</span> : null}
                      {gap.requiredForEvidenceLevel ? <span>Required: {gap.requiredForEvidenceLevel}</span> : null}
                      {gap.collectedAfter ? <span>Collected after: {gap.collectedAfter}</span> : null}
                    </div>
                  </article>
                ))}
              </div>
            ) : (
              <div className="topology-path-view__evidence-empty">
                No evidence gaps were provided for {evidenceScopePathId === 'all' ? 'the visible path set' : shortPathLabel(evidenceScopePathId)}.
              </div>
            )}
          </div>
        ) : (
          <div className="topology-path-view__zoom-layer" style={{ width: zoomedWidth, height: zoomedHeight }}>
            <div
              className="topology-path-view__stage"
              style={{
                width: model.width,
                height: model.height,
                transform: `scale(${zoom})`,
                transformOrigin: '0 0'
              }}
            >
          <svg className="topology-path-view__edges" width={model.width} height={model.height} aria-hidden="true">
            <defs>
              <marker id={markerIdForKind('path')} viewBox="0 0 10 10" refX="9" refY="5" markerWidth="7" markerHeight="7" orient="auto-start-reverse">
                <path d="M 0 0 L 10 5 L 0 10 z" fill="context-stroke" />
              </marker>
              <marker id={markerIdForKind('support')} viewBox="0 0 10 10" refX="9" refY="5" markerWidth="6" markerHeight="6" orient="auto-start-reverse">
                <path d="M 0 0 L 10 5 L 0 10 z" fill="context-stroke" />
              </marker>
            </defs>
            {visibleEdges.map((edge) => {
              const labelPoint = edgeLabelPoint(edge);
              const selected = selectedEdgeIds?.has(edge.id);
              const shared = edgeSharedWithOtherPaths(edge, renderSelectedPathId);
              const context = edgeUntaggedGapContext(edge, renderSelectedPathId);
              return (
                <g
                  key={edge.id}
                  className={[
                    'topology-path-view__edge-group',
                    selected ? 'is-selected' : '',
                    shared ? 'is-shared' : '',
                    context ? 'is-context' : ''
                  ]
                    .filter(Boolean)
                    .join(' ')}
                >
                  <path
                    className={`topology-path-view__edge topology-path-view__edge--${edge.kind}`}
                    d={edgePath(edge)}
                    stroke={edge.color}
                    markerEnd={edge.kind === 'gap' || !edge.edge.directed ? undefined : `url(#${markerIdForKind(edge.kind)})`}
                    onClick={(event) => {
                      event.stopPropagation();
                      onEdgeSelect?.(event, edge.id);
                    }}
                  />
                  {edge.label ? (
                    <foreignObject x={labelPoint.x - 56} y={labelPoint.y - 13} width="112" height="26">
                      <button
                        type="button"
                        className="topology-path-view__edge-label"
                        title={edge.label}
                        onClick={(event) => {
                          event.stopPropagation();
                          onEdgeSelect?.(event, edge.id);
                        }}
                      >
                        {edge.pathIds.length > 0 && !edge.hasExplicitLabel ? `${shortPathLabel(edge.pathIds[0])} · ` : ''}
                        {edge.label}
                      </button>
                    </foreignObject>
                  ) : null}
                </g>
              );
            })}
          </svg>
          {visibleCards.map((card) => {
            const selected = selectedNodeIds?.has(card.nodeId);
            const active = cardMatchesPath(card, renderSelectedPathId);
            const shared = cardSharedWithOtherPaths(card, renderSelectedPathId);
            const showPathBadge =
              renderSelectedPathId === 'all' &&
              cardTraceRole(card) === undefined &&
              (model.pathIds.length > 1 || card.pathIds.length === 0);
            // Device interface anchors are computed against a fixed header block, so
            // keep a hidden badge slot even in single-path views to preserve that geometry.
            const reservePathBadgeSpace = !showPathBadge && card.kind === 'device';
            return (
              <button
                key={card.id}
                type="button"
                className={[
                  'topology-path-view__card',
                  `topology-path-view__card--${card.kind}`,
                  card.cloudResource ? `topology-path-view__card--cloud-${card.cloudResource}` : '',
                  selected ? 'is-selected' : '',
                  shared ? 'is-shared' : '',
                  active ? '' : 'is-dimmed'
                ]
                  .filter(Boolean)
                  .join(' ')}
                style={{ left: card.x, top: card.y, width: card.width, height: card.height }}
                onClick={(event) => {
                  event.stopPropagation();
                  onNodeSelect?.(event, card.nodeId);
                }}
              >
                <span className="topology-path-view__card-kicker">{cardKicker(card)}</span>
                <span className="topology-path-view__card-title">{cardTitle(card)}</span>
                {cardDetail(card) ? <span className="topology-path-view__card-detail">{cardDetail(card)}</span> : null}
                {showPathBadge ? (
                  <span className="topology-path-view__path-badge">
                    {pathMembershipLabel(card.pathIds, renderSelectedPathId)}
                  </span>
                ) : reservePathBadgeSpace ? (
                  <span className="topology-path-view__path-badge-slot" aria-hidden="true" />
                ) : null}
                {card.kind === 'subnet' && card.vlanChips.length > 0 ? (
                  <span className="topology-path-view__subnet-context" aria-label={`${cardTitle(card)} subnet context`}>
                    {card.vlanChips.map((vlan) => (
                      <span key={vlan.id} className="topology-path-view__subnet-chip" title={displayName(vlan)}>
                        {displayName(vlan)}
                      </span>
                    ))}
                  </span>
                ) : null}
                {card.kind === 'device' ? (
                  <span className="topology-path-view__embedded">
                    {card.vrfGroups.map((group) => (
                      <span key={group.node.id} className="topology-path-view__vrf-group">
                        <span className="topology-path-view__vrf-title">{displayName(group.node)}</span>
                        <span className="topology-path-view__interface-list">
                          {group.interfaces.map((intf) => renderInterfaceChip(intf, group.interfaceLayoutById[intf.id]))}
                        </span>
                      </span>
                    ))}
                    {card.vpnGroups.map((group) => (
                      <span key={group.node.id} className="topology-path-view__vrf-group topology-path-view__vpn-group">
                        <span className="topology-path-view__vrf-title">VPN {displayName(group.node)}</span>
                        <span className="topology-path-view__interface-list">
                          {group.interfaces.map((tunnel) => renderInterfaceChip(tunnel, group.interfaceLayoutById[tunnel.id]))}
                        </span>
                      </span>
                    ))}
                    {card.interfaces.length > 0 ? (
                      <span className="topology-path-view__interface-list topology-path-view__interface-list--ungrouped">
                        {card.interfaces.map((intf) => renderInterfaceChip(intf, card.interfaceLayoutById[intf.id]))}
                      </span>
                    ) : null}
                  </span>
                ) : null}
              </button>
            );
          })}
          <svg className="topology-path-view__ports" width={model.width} height={model.height} aria-hidden="true">
            <defs>
              <linearGradient id={dualRolePortGradientId} x1="0%" y1="0%" x2="100%" y2="0%">
                <stop offset="50%" stopColor="#ea580c" />
                <stop offset="50%" stopColor="#16a34a" />
              </linearGradient>
            </defs>
            {visiblePorts.map((port) => (
              <circle
                key={port.key}
                className={`topology-path-view__port topology-path-view__port--${port.role}`}
                cx={port.x}
                cy={port.y}
                r={INTERFACE_PORT_RADIUS}
                fill={port.role === 'source-destination' ? `url(#${dualRolePortGradientId})` : undefined}
              />
            ))}
          </svg>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default PathRelatedViewRenderer;
